import axios from 'axios'
import { PrivateApiCall, PrivateApiCallMessaging, PublicApiCall } from "./interceptors";
import Compressor from 'compressorjs';
import { deviceInfoHeaders } from '../utils/deviceInfo';

const errorCatch = (error) => {
  // A request cancelled by the app itself (e.g. a filter change superseding
  // an in-flight fetch) is expected SPA behavior, not a real error — logging
  // it as "ERROR API CALL" just buries genuine failures in console noise.
  if (!axios.isCancel(error)) {
    console.log("ERROR API CALL", error, error?.response);
  }
  if (error.response) {
    if (error.reponse?.data) {
      return error.response?.data;
    }
    return error.response;
  } else {
    return error;
  }
};

/** ******************************************************************************************* */
/** ************************************** AUTH *********************************************** */
/** ******************************************************************************************* */
async function login(data, coords) {
  return await PublicApiCall.post("/auth/loginAdmin", data, { headers: deviceInfoHeaders(coords) })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function forgotPass(uuid, data) {
  return await PrivateApiCall.post(`/admin/users/${uuid}/resetPassword`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function otp(data) {
  return await PrivateApiCall.post(`/auth/otp`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function verifyOTP(data) {
  return await PrivateApiCall.post(`/auth/verifyAdmin`, data, { headers: deviceInfoHeaders() })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************** ME-PROFILE ***************************************** */
/** ******************************************************************************************* */
async function getMeProfile() {
  return await PrivateApiCall.get("/admin/me")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editMeProfile(data) {
  return await PrivateApiCall.put(`/admin/me`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************** ADMIN-PROFILES ************************************* */
/** ******************************************************************************************* */
async function getAdminProfiles() {
  return await PrivateApiCall.get("/admin/admin-profiles")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addAdminProfile(data) {
  return await PrivateApiCall.post("/admin/admin-profile", data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editAdminProfile(data, uuid) {
  return await PrivateApiCall.put(`/admin/admin-profile/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteAdminProfile(uuid) {
  return await PrivateApiCall.delete(`/admin/admin-profile/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************** RESTAURANT ***************************************** */
/** ******************************************************************************************* */
async function getRestaurants() {
  return await PrivateApiCall.get("/admin/restaurant")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addRestaurant(data) {
  return await PrivateApiCall.post("/admin/restaurant", data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editRestaurant(data, uuid) {
  return await PrivateApiCall.put(`/admin/restaurant/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteRestaurant(uuid) {
  return await PrivateApiCall.delete(`/admin/restaurant/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************** BRANCHES ******************************************* */
/** ******************************************************************************************* */
async function getBranches(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getAllBranches() {
  return await PrivateApiCall.get(`/admin/branch`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getBranchesOrders(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/orders${ruuid ? `/${ruuid}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addBranch(ruuid, data) {
  return await PrivateApiCall.post(`/admin/branch/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editBranch(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/branch/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteBranch(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/branch/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchesTableOrdering(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/tableOrdering/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchesLoyalty(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/loyalty/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchesQRMenu(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/qrMenu/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchesFloorSetup(ruuid) {
  return await PrivateApiCall.get(`/admin/branch/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}


/**
 * Branches enabled for any of the given customer modules — repeated `module` params
 * (`?module=onlineDelivery&module=pos`) are OR-ed by the backend. Keys must match
 * `customerModules` in the backend's enums/shared/modules.enums.js.
 * Returns the same `{ branches }` shape as the per-module endpoints it replaces.
 */
async function getBranchesByModule(ruuid, modules = []) {
  const query = modules
    .filter(Boolean)
    .map((m) => `module=${encodeURIComponent(m)}`)
    .join('&');
  return await PrivateApiCall.get(`/admin/branch/module/${ruuid}${query ? `?${query}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getBranchesOnlineDelivery(ruuid) {
  return await getBranchesByModule(ruuid, ['onlineDelivery']);
}

async function getBranchesPos(ruuid) {
  return await getBranchesByModule(ruuid, ['pos']);
}

async function getBranchesAiChatbot(ruuid) {
  return await getBranchesByModule(ruuid, ['aiChatbot']);
}

/** ******************************************************************************************* */
/** ************************************** ENTITIES ******************************************* */
/** ******************************************************************************************* */
async function getEntities() {
  return await PrivateApiCall.get('/admin/branch/group')
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addEntity(data) {
  return await PrivateApiCall.post('/admin/branch/group', data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editEntity(uuid, data) {
  return await PrivateApiCall.put(`/admin/branch/group/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateEntities(data) {
  return await PrivateApiCall.put('admin/branch/group-bulk', data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteEntity(uuid) {
  return await PrivateApiCall.delete(`/admin/branch/group/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ***************************************************************************************** */
/** ************************************* CATEGORY ***************************************** */
/** **************************************************************************************** */
//RI NEED CORRECTION
async function getCategories(ruuid) {
  return await PrivateApiCall.get(`/admin/productCategory/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addCategory(ruuid, data) {
  return await PrivateApiCall.post(`/admin/productCategory/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editCategory(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/productCategory/${ruuid}/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteCategory(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/productCategory/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateCategories(ruuid, data) {
  return await PrivateApiCall.put(`/admin/productCategory/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function importCategories(ruuid, data) {
  return await PrivateApiCall.post(`/admin/productCategory/import/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateCategoryImages(ruuid, data) {
  return await PrivateApiCall.put(`/admin/productCategory/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddCategories(ruuid, data) {
  return await PrivateApiCall.post(`/admin/productCategory/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ***************************************************************************************** */
/** ************************************* GROUP  ******************************************** */
/** ***************************************************************************************** */
//RI NEED CORRECTION
async function getGroups(ruuid) {
  return await PrivateApiCall.get(`/admin/productGroup/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateGroups(ruuid, data) {
  return await PrivateApiCall.put(`/admin/productGroup/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addGroup(ruuid, data) {
  return await PrivateApiCall.post(`/admin/productGroup/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editGroup(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/productGroup/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteGroup(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/productGroup/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkEditGroups(ruuid, data) {
  return await PrivateApiCall.put(`/admin/productGroup/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddGroups(ruuid, data) {
  return await PrivateApiCall.post(`/admin/productGroup/bulk/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportGroupsExcel(ruuid) {
  return await PrivateApiCall.get(`/admin/productGroup/export/template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importGroupsExcel(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/productGroup/import/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** TAXES ********************************************** */
/** ******************************************************************************************* */
async function getTaxes() {
  return await PrivateApiCall.get(`/admin/tax`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addTax(data) {
  return await PrivateApiCall.post(`/admin/tax`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editTax(uuid, data) {
  return await PrivateApiCall.put(`/admin/tax/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteTax(uuid) {
  return await PrivateApiCall.delete(`/admin/tax/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchTaxes(ruuid) {
  return await PrivateApiCall.get(`/admin/branch-taxing/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateBranchTaxes(ruuid, data) {
  return await PrivateApiCall.put(`/admin/branch-taxing/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getBranchChargeTaxes(ruuid) {
  return await PrivateApiCall.get(`/admin/branch-charge-taxes/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateBranchChargeTaxes(ruuid, data) {
  return await PrivateApiCall.put(`/admin/branch-charge-taxes/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToProducts(ruuid, data) {
  return await PrivateApiCall.put(`/admin/product/${ruuid}/apply-taxes`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getProductsWithTaxes(ruuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}/taxes`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToAllProducts(ruuid, data) {
  return await PrivateApiCall.put(
    `/admin/product/${ruuid}/apply-taxes-to-all`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getStockItemsWithTaxes() {
  return await PrivateApiCall.get(`/admin/SmdItem/taxes`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToStockItems(data) {
  return await PrivateApiCall.put(`/admin/SmdItem/apply-taxes`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getPurchasedProductsWithTaxes() {
  return await PrivateApiCall.get(`/admin/SmdLinkedItem/taxes`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToPurchasedProducts(data) {
  return await PrivateApiCall.put(`/admin/SmdLinkedItem/apply-taxes`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getSuppliersWithTaxes() {
  return await PrivateApiCall.get(`/admin/smdSupplier/taxes`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToSuppliers(data) {
  return await PrivateApiCall.put(`/admin/smdSupplier/apply-taxes`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getModifiersWithTaxes(ruuid) {
  return await PrivateApiCall.get(`/admin/modifier/${ruuid}/taxes`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function applyTaxesToModifiers(ruuid, data) {
  return await PrivateApiCall.put(`/admin/modifier/${ruuid}/apply-taxes`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************** Ingredients ********************************************** */
/** ******************************************************************************************* */
async function getIngredients() {
  return await PrivateApiCall.get(`/admin/ingredient`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addIngredient(data) {
  return await PrivateApiCall.post(`/admin/ingredient`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editIngredient(uuid, data) {
  return await PrivateApiCall.put(`/admin/ingredient/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteIngredient(uuid) {
  return await PrivateApiCall.delete(`/admin/ingredient/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateIngredients(data) {
  return await PrivateApiCall.put(`/admin/ingredient/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateIngredientImages(data) {
  return await PrivateApiCall.put(`/admin/ingredient/images`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddIngredients(data) {
  return await PrivateApiCall.post(`/admin/ingredient/bulk`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportIngredientsExcel() {
  return await PrivateApiCall.get(`/admin/ingredient/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importIngredientsExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/ingredient/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************TYPES AND ALLERGIES *********************************** */
/** ******************************************************************************************* */
async function getTypesAndAllergies(ruuid) {
  return await PrivateApiCall.get(`/admin/foodTypesAndAlergies/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getSingleTypeAndAllergies(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/foodTypesAndAlergies/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addTypesAndAllergies(ruuid, data) {
  return await PrivateApiCall.post(`/admin/foodTypesAndAlergies/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editTypesAndAllergies(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/foodTypesAndAlergies/${ruuid}/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteTypesAndAllergies(ruuid, uuid) {
  return await PrivateApiCall.delete(
    `/admin/foodTypesAndAlergies/${ruuid}/${uuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateTypesAndAllergies(ruuid, data) {
  return await PrivateApiCall.put(`/admin/foodTypesAndAlergies/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateTypes(ruuid, data) {
  return await PrivateApiCall.put(`/admin/foodTypesAndAlergies/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddTypes(ruuid, data) {
  return await PrivateApiCall.post(`/admin/foodTypesAndAlergies/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportFoodTypesExcel(ruuid) {
  return await PrivateApiCall.get(`/admin/foodTypesAndAlergies/export/template/${ruuid}/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importFoodTypesExcel(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/foodTypesAndAlergies/import/${ruuid}/`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** PRODUCTS ******************************************* */
/** ******************************************************************************************* */
async function getProducts(ruuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getProductsPaginated(ruuid, params, cancelToken) {
  const safeParams = params || {};
  const queryParams = new URLSearchParams();

  queryParams.append('page', safeParams.page || 1);
  queryParams.append('limit', safeParams.limit || 10);

  if (safeParams.search) queryParams.append('search', safeParams.search);
  if (safeParams.productCategory) queryParams.append('productCategory', safeParams.productCategory);
  // Handle productGroup as array to support multiple groups
  if (safeParams.productGroup) {
    if (Array.isArray(safeParams.productGroup)) {
      safeParams.productGroup.forEach(group => {
        queryParams.append('productGroup', group);
      });
    } else {
      queryParams.append('productGroup', safeParams.productGroup);
    }
  }
  if (safeParams.type) queryParams.append('type', safeParams.type);
  if (safeParams.posProductType !== undefined && safeParams.posProductType !== null && String(safeParams.posProductType).trim() !== '') {
    queryParams.append('posProductType', safeParams.posProductType);
  }
  if (safeParams.minPrice !== undefined && safeParams.minPrice !== null) queryParams.append('minPrice', safeParams.minPrice);
  if (safeParams.maxPrice !== undefined && safeParams.maxPrice !== null) queryParams.append('maxPrice', safeParams.maxPrice);
  if (safeParams.priceModule) queryParams.append('priceModule', safeParams.priceModule);
  if (safeParams.isActive !== undefined && safeParams.isActive !== null) queryParams.append('isActive', safeParams.isActive);
  if (safeParams.sortBy) queryParams.append('sortBy', safeParams.sortBy);
  if (safeParams.sortOrder) queryParams.append('sortOrder', safeParams.sortOrder);
  if (safeParams.includeModifiers !== undefined) queryParams.append('includeModifiers', safeParams.includeModifiers);
  if (safeParams.includePrinters !== undefined) queryParams.append('includePrinters', safeParams.includePrinters);
  if (safeParams.includeCourses !== undefined) queryParams.append('includeCourses', safeParams.includeCourses);
  if (safeParams.includeFormulas !== undefined) queryParams.append('includeFormulas', safeParams.includeFormulas);
  if (safeParams.includeTimePricing !== undefined) queryParams.append('includeTimePricing', safeParams.includeTimePricing);

  const queryString = queryParams.toString();
  const url = `/admin/product/paginated/${ruuid}${queryString ? `?${queryString}` : ''}`;

  const config = cancelToken ? { cancelToken } : {};

  return await PrivateApiCall.get(url, config)
    .then((response) => response)
    .catch((error) => {
      // Don't treat canceled requests as errors
      if (axios.isCancel && axios.isCancel(error)) {
        return Promise.reject(error);
      }
      return errorCatch(error);
    });
}

async function getProduct(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getProductMaxPrice(ruuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}/max-price`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addProduct(ruuid, data) {
  return await PrivateApiCall.post(`/admin/product/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editProduct(data, ruuid, uuid) {
  return await PrivateApiCall.put(`/admin/product/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateProducts(ruuid, data) {
  return await PrivateApiCall.put(`/admin/product/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateProductMediaGallery(ruuid, data) {
  return await PrivateApiCall.put(`/admin/product/media-gallery/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteProduct(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/product/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateProductPriceLevelPrices(ruuid, productUuid, data) {
  return await PrivateApiCall.put(
    `/admin/product/${ruuid}/${productUuid}/price-level-prices`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteProductImage(ruuid, uuid) {
  return await PrivateApiCall.put(
    `/admin/product/${ruuid}/${uuid}/delete-image`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editProductImage(ruuid, uuid, data) {
  return await PrivateApiCall.post(
    `/admin/upload/${ruuid}/product-image/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editBulkProducts(ruuid, data) {
  return await PrivateApiCall.put(`/admin/product/${ruuid}/bulk-update`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addBulkProducts(ruuid, data) {
  return await PrivateApiCall.post(`/admin/product/${ruuid}/bulk-add`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getRewardsProducts(ruuid) {
  return await PrivateApiCall.get(`/admin/product/rewards/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAddEditProducts(ruuid) {
  return await PrivateApiCall.get(`/admin/product/product-add-edit/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function generateImage(data) {
  return await PrivateApiCall.post(`/admin/product/generate-image`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function uploadProductMediaGallery(ruuid, data) {
  const formData = new FormData();
  formData.append('media', data);
  return await PrivateApiCall.post(`/admin/upload/${ruuid}/product-media-gallery`, formData)
    .then(response => response)
    .catch(error => errorCatch(error));
}

async function importProducts(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/product/import/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductExelTemplate(ruuid) {
  return await PrivateApiCall.get(`/admin/product/export/template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** MODIFIERS ********************************************** */
/** ******************************************************************************************* */
async function getModifiers(ruuid) {
  return await PrivateApiCall.get(`/admin/modifier/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getModifier(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/modifier/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addModifier(ruuid, data) {
  return await PrivateApiCall.post(`/admin/modifier/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editModifier(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/modifier/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteModifier(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/modifier/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateModifiers(ruuid, data) {
  return await PrivateApiCall.put(`/admin/modifier/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateModifiers(ruuid, data) {
  return await PrivateApiCall.put(`/admin/modifier/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkAddModifiers(ruuid, data) {
  return await PrivateApiCall.post(`/admin/modifier/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function exportModifiersExcel(ruuid) {
  return await PrivateApiCall.get(`/admin/modifier/export/template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importModifiersExcel(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/modifier/import/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** MODIFIERSGROUPS ********************************************** */
/** ******************************************************************************************* */
async function getModifiersGroups(ruuid) {
  return await PrivateApiCall.get(`/admin/modifierGroups/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getModifierGroup(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/modifierGroups/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getModifierGroupWithModifiers(ruuid, uuid) {
  return await PrivateApiCall.get(
    `/admin/modifierGroups/${ruuid}/${uuid}/modifiers`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getModifiersGroupsWithModifiers(ruuid) {
  return await PrivateApiCall.get(`/admin/modifierGroups/${ruuid}/modifiers`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addModifierGroup(ruuid, data) {
  return await PrivateApiCall.post(`/admin/modifierGroups/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editModifierGroup(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/modifierGroups/${ruuid}/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteModifierGroup(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/modifierGroups/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkUpdateModifierGroups(ruuid, data) {
  return await PrivateApiCall.put(`/admin/modifierGroups/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkAddModifierGroups(ruuid, data) {
  return await PrivateApiCall.post(`/admin/modifierGroups/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function exportModGroupsExcel(ruuid) {
  return await PrivateApiCall.get(`/admin/modifierGroups/export/template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importModGroupsExcel(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/modifierGroups/import/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setModifierGroupImages(ruuid, data) {
  return await PrivateApiCall.put(`/admin/modifierGroups/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************** ROLES ********************************************** */
/** ******************************************************************************************* */
async function getRoles(section) {
  return await PrivateApiCall.get(`/admin/role${section ? `?section=${section}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addRole(data) {
  return await PrivateApiCall.post(`/admin/role`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editRole(uuid, data, section) {
  return await PrivateApiCall.put(`/admin/role/${uuid}${section ? `?section=${section}` : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteRole(uuid) {
  return await PrivateApiCall.delete(`/admin/role/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getRolesHierarchy() {
  return await PrivateApiCall.get(`/admin/role/hierarchy`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** roles: [{ uuid, parentUuid (null = top level), name? }] — saved together, all or nothing. */
async function editRolesHierarchy(roles) {
  return await PrivateApiCall.put(`/admin/role/hierarchy`, { roles })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function assignRoleUsers(uuid, userUuids) {
  return await PrivateApiCall.put(`/admin/role/${uuid}/users`, { userUuids })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}


/** ******************************************************************************************* */
/** ************************************** POS ROLES ****************************************** */
/** ******************************************************************************************* */

async function getRolePosPermissions(roleUuid) {
  return await PrivateApiCall.get(`/admin/role/${roleUuid}/pos-permissions`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateRolePosPermissions(roleUuid, data) {
  return await PrivateApiCall.put(`/admin/role/${roleUuid}/pos-permissions`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************** USERS ********************************************** */
/** ******************************************************************************************* */
async function getUsers() {
  return await PrivateApiCall.get(`/admin/users/adminUsers`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getCombinedUsers() {
  return await PrivateApiCall.get(`/admin/users/adminUsers-combined`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addUser(data) {
  return await PrivateApiCall.post(`/admin/users/adminUsers`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addCombinedUsers(data) {
  return await PrivateApiCall.post(`/admin/users/adminUsers/combined`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editUser(uuid, data) {
  return await PrivateApiCall.put(`/admin/users/edit/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editCombinedUsers(data) {
  return await PrivateApiCall.put(`/admin/users/adminUsers/combined`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteUser(uuid) {
  return await PrivateApiCall.delete(`/admin/users/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function exportCombinedUsersExcel() {
  return await PrivateApiCall.get(`/admin/users/adminUsers/combined/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function importCombinedUsersExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/users/adminUsers/combined/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateAdminUsersImages(data) {
  return await PrivateApiCall.put(`/admin/users/images`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editUserSettings(data) {
  return await PrivateApiCall.put(`/admin/users/settings`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function changeAccountActivity(uuid, data) {
  return await PrivateApiCall.post(`/admin/users/${uuid}/status`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function changeAccountVerification(uuid, data) {
  return await PrivateApiCall.post(`/admin/users/${uuid}/verification`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
// Restrict device access (per admin account) + Users → Devices
async function changeUserDeviceRestriction(uuid, data) {
  // Device header so the backend approves this device when admins restrict their own account.
  return await PrivateApiCall.put(`/admin/users/${uuid}/device-restriction`, data, { headers: deviceInfoHeaders() })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getUserDevices() {
  return await PrivateApiCall.get(`/admin/users/devices`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function approveUserDevice(uuid) {
  return await PrivateApiCall.put(`/admin/users/devices/${uuid}/approve`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function rejectUserDevice(uuid) {
  return await PrivateApiCall.put(`/admin/users/devices/${uuid}/reject`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function deleteUserDevice(uuid) {
  return await PrivateApiCall.delete(`/admin/users/devices/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
// Makes the device the only one its account can punch in / out from in Attendance.
async function setUserPunchDevice(uuid) {
  return await PrivateApiCall.put(`/admin/users/devices/${uuid}/punch-device`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/**
 * Users → Attendance Settings (RESTOAPP-7527): the restaurant's attendance settings and each
 * branch's punch zones, on the same API the stock app's Attendance page used.
 */
async function getSmaAttendanceSettings(ruuid) {
  return await PrivateApiCall.get(`/admin/sma-attendance/${ruuid}/settings`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** `{ workStartTime, graceMinutes, workDays, requireLocation, radiusMeters }` — every field, every time. */
async function updateSmaAttendanceSettings(ruuid, data) {
  return await PrivateApiCall.put(`/admin/sma-attendance/${ruuid}/settings`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** `{ branches: [{ uuid, location }], zones: [{ uuid, branchUuid, name, type, coordinates, isActive }] }` */
async function getSmaAttendanceZones(ruuid) {
  return await PrivateApiCall.get(`/admin/sma-attendance/${ruuid}/zones`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** The branch's full set of zones: known uuids are updated, the rest created, the ones left out deleted. */
async function saveSmaAttendanceZones(ruuid, buuid, zones) {
  return await PrivateApiCall.put(`/admin/sma-attendance/${ruuid}/${buuid}/zones`, { zones })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/**
 * Users → Departments + Users → Staff Schedules (RESTOAPP-7597 / 7598), organisation-wide.
 * Config: `{ shiftTypes, departments (with staffCount), requirements, shiftLimits }`.
 */
async function getStaffScheduleConfig() {
  return await PrivateApiCall.get(`/admin/staff-schedule/config`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** `{ name, shortName, startTime, endTime, color, isActive }` (times `HH:mm`). */
async function addStaffShiftType(data) {
  return await PrivateApiCall.post(`/admin/staff-schedule/shift-types`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStaffShiftType(uuid, data) {
  return await PrivateApiCall.put(`/admin/staff-schedule/shift-types/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStaffShiftType(uuid) {
  return await PrivateApiCall.delete(`/admin/staff-schedule/shift-types/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** `{ name, color }` */
async function addStaffDepartment(data) {
  return await PrivateApiCall.post(`/admin/staff-schedule/departments`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStaffDepartment(uuid, data) {
  return await PrivateApiCall.put(`/admin/staff-schedule/departments/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStaffDepartment(uuid) {
  return await PrivateApiCall.delete(`/admin/staff-schedule/departments/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Every-day counts: `[{ departmentUuid, shiftTypeUuid, dayOfWeek: null, requiredStaff }]`. */
async function saveStaffRequirements(requirements) {
  return await PrivateApiCall.put(`/admin/staff-schedule/requirements`, { requirements })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** `[{ employeeType, minShifts, maxShifts }]` */
async function saveStaffShiftLimits(limits) {
  return await PrivateApiCall.put(`/admin/staff-schedule/shift-limits`, { limits })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** The week starting `weekStart` (`YYYY-MM-DD`, a Monday). */
async function getStaffSchedule(weekStart) {
  return await PrivateApiCall.get(`/admin/staff-schedule/schedule?weekStart=${encodeURIComponent(weekStart)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Cell toggles: `[{ staffUuid, shiftTypeUuid, date, assigned }]`. */
async function saveStaffAssignments(changes) {
  return await PrivateApiCall.put(`/admin/staff-schedule/schedule/assignments`, { changes })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** A day's counts per department: `[{ departmentUuid, shiftTypeUuid, dayOfWeek, requiredStaff (null = every-day number) }]`. */
async function saveStaffDayRequirements(requirements) {
  return await PrivateApiCall.put(`/admin/staff-schedule/schedule/requirements`, { requirements })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function copyStaffWeek(fromWeekStart, toWeekStart) {
  return await PrivateApiCall.post(`/admin/staff-schedule/schedule/copy-week`, { fromWeekStart, toWeekStart })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** An employee type next to the built-in ones (the gear by Employee Type on the user form). */
async function addStaffEmployeeType(name) {
  return await PrivateApiCall.post(`/admin/staff-schedule/employee-types`, { name })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Clears it from the users that have it; answers `{ cleared }`. */
async function deleteStaffEmployeeType(uuid) {
  return await PrivateApiCall.delete(`/admin/staff-schedule/employee-types/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getAppUsers() {
  return await PrivateApiCall.get(`/admin/app-users/`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getAppUser(uuid) {
  return await PrivateApiCall.get(`/admin/app-users/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addAppUser(data) {
  return await PrivateApiCall.post(`/admin/app-users/add`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editAppUser(uuid, data) {
  return await PrivateApiCall.put(`/admin/app-users/${uuid}/edit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkEditAppUsers(data) {
  return await PrivateApiCall.put(`/admin/app-users/bulk-edit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkAddAppUsers(data) {
  return await PrivateApiCall.post(`/admin/app-users/bulk-add`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteAppUser(uuid) {
  return await PrivateApiCall.put(`/admin/app-users/${uuid}/delete`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateAppUsersImages(data) {
  return await PrivateApiCall.put(`/admin/app-users/images`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getRestaurantsWithBranches() {
  return await PrivateApiCall.get(`/admin/restaurant/branches`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportAppUsersExcel() {
  return await PrivateApiCall.get(`/admin/app-users/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importAppUsersExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/app-users/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAllCustomerCreditTransactions() {
  return await PrivateApiCall.get(`/admin/app-users/credit-transactions`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerCreditTransactions(uuid) {
  return await PrivateApiCall.get(`/admin/app-users/${uuid}/credit-transactions`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerCreditStatement(uuid) {
  return await PrivateApiCall.get(`/admin/app-users/${uuid}/credit-statement`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/**
 * Customer statement print template (edited on the stock-admin "Customer statement" print-settings page).
 * One template for the whole tenant; `design` is 'a4' or 'thermal' (80mm roll), each saved separately.
 * A backend that predates the global template only serves `/:ruuid`, so fall back to it on 404.
 */
async function getCustomerStatementPrintSettings(design = 'a4', ruuid) {
  const get = (path) => PrivateApiCall.get(`/admin/customer-statement-print-settings${path}?design=${design}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
  const response = await get('')
  if (response?.status === 404 && ruuid) return await get(`/${ruuid}`)
  return response
}

/**
 * Customer receipt print template (edited on the stock-admin "Customer receipt" print-settings page).
 * One template per restaurant; `design` is 'a4' or 'thermal' (80mm roll), each saved separately.
 */
async function getCustomerReceiptPrintSettings(ruuid, design = 'a4') {
  return await PrivateApiCall.get(`/admin/customer-receipt-print-settings/${ruuid}?design=${design}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** POS › Print Network › Customer receipt design (RESTOAPP-7366): saves one design (`a4` | `thermal`). */
async function saveCustomerReceiptPrintSettings(ruuid, data, design = 'a4') {
  return await PrivateApiCall.post(`/admin/customer-receipt-print-settings/${ruuid}?design=${design}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** One B2B sales order / invoice (stock-admin B2B Sales), in the same shape the stock app prints from. */
async function getSmaB2bOrder(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/sma-b2b-order/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** B2B sales invoice print template (stock-admin print settings → Sales). */
async function getSalesPrintSettings(ruuid) {
  return await PrivateApiCall.get(`/admin/sales-print-settings/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function postCustomerCreditPayment(uuid, data) {
  return await PrivateApiCall.post(`/admin/app-users/${uuid}/credit-payments`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setCustomerCreditLimit(uuid, data) {
  return await PrivateApiCall.put(`/admin/app-users/${uuid}/credit-limit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/**
 * Customer aging (accounts receivable) — see `docs/admin-customer-aging-api.md` in main-backend.
 *
 * Nothing here is stored: due dates are derived at read time from the customer's `paymentTerms` and
 * each credit sale's date, so an "item" is one *instalment* of one sale (identity `saleUuid` +
 * `installmentIndex`) and receipts settle oldest-sale-first.
 *
 * `params`: { asOf, status, q, customerUuid, branchUuid, includeFuture, includePaid }, where
 * `status` is unpaid | dueAndOverdue | overdue | dueToday | upcoming | paid | all.
 */
function customerAgingQuery(params) {
  const search = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    search.append(key, String(value))
  })
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

async function getAllCustomerAging(params) {
  return await PrivateApiCall.get(`/admin/app-users/aging${customerAgingQuery(params)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerAging(uuid, params) {
  return await PrivateApiCall.get(`/admin/app-users/${uuid}/aging${customerAgingQuery(params)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** POS Users ********************************************** */
/** ******************************************************************************************* */
async function getPosUsers() {
  return await PrivateApiCall.get(`/admin/pos-user`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getPosUser(uuid, data) {
  return await PrivateApiCall.get(`/admin/pos-user//${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addPosUser(data) {
  return await PrivateApiCall.post(`/admin/pos-user`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editPosUser(uuid, data) {
  return await PrivateApiCall.put(`/admin/pos-user/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deletePosUser(uuid) {
  return await PrivateApiCall.delete(`/admin/pos-user/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updatePosUsersImages(data) {
  return await PrivateApiCall.put(`/admin/pos-user/images`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getPosBranches(puuid) {
  return await PrivateApiCall.get(`/admin/pos-user/branches/${puuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getAllPosBranches() {
  return await PrivateApiCall.get(`/admin/branch/pos`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ********************************** APP USERS ADDRESSES ************************************ */
/** ******************************************************************************************* */

async function getAppUserAddresses(uuid) {
  return await PrivateApiCall.get(`/admin/app-users-address/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addAppUserAddress(uuid, data) {
  return await PrivateApiCall.post(`/admin/app-users-address/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editAppUserAddress(uuid, data) {
  return await PrivateApiCall.put(`/admin/app-users-address/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteAppUserAddress(uuid) {
  return await PrivateApiCall.delete(`/admin/app-users-address/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function parseMapLocationLink(url) {
  const trimmed = url != null ? String(url).trim() : '';
  return await PrivateApiCall.post('/admin/map-location/parse', { url: trimmed })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}


/** ******************************************************************************************* */
/** ****************************** CUSTOMER COMPLAINTS (APP USER) ***************************** */
/** ******************************************************************************************* */

async function getCustomerComplaints(custUuid) {
  return await PrivateApiCall.get(`/admin/app-users/${custUuid}/complaints`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addCustomerComplaint(custUuid, data) {
  return await PrivateApiCall.post(`/admin/app-users/${custUuid}/complaints`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editCustomerComplaint(custUuid, complaintUuid, data) {
  return await PrivateApiCall.put(`/admin/app-users/${custUuid}/complaints/${complaintUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ****************************** CUSTOMER ORDER STATS & ORDERS ****************************** */
/** ******************************************************************************************* */

async function getCustomerStats(custUuid) {
  return await PrivateApiCall.get(`/admin/app-users/${custUuid}/stats`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getCustomerOrders(custUuid, params = {}) {
  const query = new URLSearchParams();
  if (params.branchUuid) query.set("branchUuid", params.branchUuid);
  if (params.type) query.set("type", params.type);
  if (params.includeVoided != null) query.set("includeVoided", String(params.includeVoided));
  if (params.view) query.set("view", params.view);
  if (params.search) query.set("search", params.search);
  const qs = query.toString();
  return await PrivateApiCall.get(`/admin/app-users/${custUuid}/orders${qs ? `?${qs}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** *********************************** APP USERS GROUPS ************************************** */
/** ******************************************************************************************* */
async function getAppUsersGroups() {
  return await PrivateApiCall.get(`/admin/app-users-group`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addAppUsersGroup(data) {
  return await PrivateApiCall.post(`/admin/app-users-group/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function editAppUsersGroup(uuid, data) {
  return await PrivateApiCall.put(`/admin/app-users-group/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function deleteAppUsersGroup(uuid) {
  return await PrivateApiCall.delete(`/admin/app-users-group/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function bulkAddAppUsersGroups(data) {
  return await PrivateApiCall.post(`/admin/app-users-group/bulk`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function exportAppUsersGroupsExcel() {
  return await PrivateApiCall.get(`/admin/app-users-group/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function importAppUsersGroupsExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/app-users-group/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *************************************** ALLERGENS ***************************************** */
/** ******************************************************************************************* */
async function getAllergens() {
  return await PrivateApiCall.get(`/admin/allergen`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getAllergen(uuid) {
  return await PrivateApiCall.get(`/admin/allergen/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addAllergen(data) {
  return await PrivateApiCall.post(`/admin/allergen`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editAllergen(uuid, data) {
  return await PrivateApiCall.put(`/admin/allergen/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteAllergen(uuid) {
  return await PrivateApiCall.delete(`/admin/allergen/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateAllergens(data) {
  return await PrivateApiCall.put(`/admin/allergen/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportAllergensExcel() {
  return await PrivateApiCall.get(`/admin/allergen/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importAllergensExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/allergen/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *************************************** PRINTERS ***************************************** */
/** ******************************************************************************************* */
async function getPrinters(ruuid) {
  return await PrivateApiCall.get(`/admin/printer-locations/printers/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addPrinter(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/printers/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editPrinter(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/printers/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deletePrinter(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/printer-locations/printers/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ PRINTER LOCATIONS *********************************** */
/** ******************************************************************************************* */
async function getPrinterLocations(ruuid) {
  return await PrivateApiCall.get(`/admin/printer-locations/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addPrinterLocation(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editPrinterLocation(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deletePrinterLocation(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/printer-locations/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addPrinterLocationForBranch(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editPrinterLocationForBranch(ruuid, buuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/${ruuid}/${buuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function assignLocationByBranch(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/assignments-by-branch/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

// POS ROUTING ASSIGNMENTS
async function getPrinterLocationAssignments(ruuid, buuid, assignmentType) {
  let url = `/admin/printer-locations/assignments/${ruuid}`
  const params = []
  if (buuid) {
    params.push(`branchUuid=${buuid}`)
  }
  if (assignmentType) {
    params.push(`assignmentType=${assignmentType}`)
  }
  if (params.length > 0) {
    url += `?${params.join('&')}`
  }
  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setPrinterLocationAssignment(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/assignments/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosInvoiceConfigs(ruuid, buuid) {
  if (buuid) {
    return await PrivateApiCall.get(`/admin/printer-locations/pos-invoice/${ruuid}?branchUuid=${buuid}`)
      .then((response) => response)
      .catch((error) => errorCatch(error))
  }
  else {
    return await PrivateApiCall.get(`/admin/printer-locations/pos-invoice/${ruuid}`)
      .then((response) => response)
      .catch((error) => errorCatch(error))
  }
}

async function setPosInvoiceConfig(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/pos-invoice/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosBackScreens(ruuid, buuid) {
  if (buuid) {
    return await PrivateApiCall.get(`/admin/printer-locations/pos-back-screen/${ruuid}?branchUuid=${buuid}`)
      .then((response) => response)
      .catch((error) => errorCatch(error))
  }
  else {
    return await PrivateApiCall.get(`/admin/printer-locations/pos-back-screen/${ruuid}`)
      .then((response) => response)
      .catch((error) => errorCatch(error))
  }
}

async function bulkSetPrinterLocations(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/bulk-set/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setPrinterLocationOutputRouting(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/output-routing/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getWorkstations(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/workstation/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateWorkstation(ruuid, buuid, wuuid, data) {
  return await PrivateApiCall.put(`/admin/workstation/${ruuid}/${buuid}/${wuuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getKitchenMonitors(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/kitchen-monitor/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getKitchenMonitor(ruuid, buuid, uuid) {
  return await PrivateApiCall.get(`/admin/kitchen-monitor/${ruuid}/${buuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editKitchenMonitor(ruuid, buuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/kitchen-monitor/${ruuid}/${buuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getBackScreens(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/back-screen/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getBackScreen(ruuid, buuid, uuid) {
  return await PrivateApiCall.get(`/admin/back-screen/${ruuid}/${buuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editBackScreen(ruuid, buuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/back-screen/${ruuid}/${buuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Per-device list (one row per kiosk device slot licensed for the branch) — same shape as kitchen-monitor/back-screen. */
async function getSelfOrderingDevices(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/self-ordering/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editSelfOrderingDevice(ruuid, buuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering/${ruuid}/${buuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPrinterLocationsConfig(ruuid, buuid) {
  return await PrivateApiCall.get(`admin/printer-locations/config/${ruuid}/?branchUuid=${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setPrinterLocationsConfig(ruuid, buuid, data) {
  return await PrivateApiCall.post(`admin/printer-locations/config/${ruuid}/?branchUuid=${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// ZONES
async function getZones(ruuid) {
  return await PrivateApiCall.get(`/admin/printer-locations/zones/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addZone(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/zones/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editZone(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/zones/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteZone(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/printer-locations/zones/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddZones(ruuid, data) {
  return await PrivateApiCall.post(`/admin/printer-locations/zones/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkEditZones(ruuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/zones/bulk/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNetworkDiagramData(ruuid, branchUuid) {
  const baseUrl = `/admin/printer-locations/network-diagram/${ruuid}`
  const url = branchUuid ? `${baseUrl}?branchUuid=${branchUuid}` : baseUrl
  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ VIRTUAL LOCATIONS *********************************** */
/** ******************************************************************************************* */
// Virtual locations are org-global: the list route takes no restaurant (a `/:ruuid/`
// suffix would now hit the get-one route and 404).
async function getVirtualLocations(ruuid) {
  // Virtual locations are org-global. Optional ?ruuid= only narrows legacy restaurant-scoped rows.
  const url = ruuid
    ? `/admin/virtual-locations/?ruuid=${encodeURIComponent(ruuid)}`
    : `/admin/virtual-locations/`
  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getPrinterLocationProducts(ruuid) {
  return await PrivateApiCall.get(`/admin/printer-locations/products/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getPrinterLocationProductGroups(ruuid) {
  return await PrivateApiCall.get(`/admin/printer-locations/product-groups/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkUpdateProductLocations(ruuid, data) {
  return await PrivateApiCall.put(`/admin/printer-locations/bulk-update-product-locations/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function assignProductsToVirtualLocation(ruuid, data) {
  return await PrivateApiCall.put(`/admin/virtual-locations/products/bulk/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkAddAllergens(data) {
  return await PrivateApiCall.post(`/admin/allergen/bulk`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateAllergensImages(data) {
  return await PrivateApiCall.put(`/admin/allergen/images/upload`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ******************************************* MENU ****************************************** */
/** ******************************************************************************************* */
async function getMenu(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/menu/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getMenus(ruuid) {
  return await PrivateApiCall.get(`/admin/menu/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getMenuAssignments(ruuid) {
  return await PrivateApiCall.get(`/admin/menu/${ruuid}/assignments`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
// Every restaurant's menus + every branch's assignments in one call (menu assignment screen)
async function getAllMenuAssignments() {
  return await PrivateApiCall.get(`/admin/menu/assignments`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkUpsertMenuAssignments(ruuid, data) {
  return await PrivateApiCall.post(`/admin/menu/${ruuid}/assignments/bulk`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function bulkUpsertMenuAssignmentsMultiRestaurant(data) {
  return await PrivateApiCall.post(`/admin/menu/assignments/bulk`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addMenu(ruuid, data) {
  return await PrivateApiCall.post(`/admin/menu/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editMenu(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/menu/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteMenu(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/menu/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editMenuName(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/menu/${ruuid}/${uuid}/update-name`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateMenuSorting(ruuid, data) {
  return await PrivateApiCall.put(`/admin/menu/${ruuid}/update-order`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** Menu import (AI extraction) — restaurant-scoped */
async function listMenuImports(ruuid) {
  return await PrivateApiCall.get(`/admin/menu-imports/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getMenuImport(ruuid, importUuid) {
  return await PrivateApiCall.get(`/admin/menu-imports/${ruuid}/${importUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function createMenuImportMultipart(ruuid, files) {
  const formData = new FormData();
  (files || []).forEach((file) => {
    formData.append("files", file);
  });
  return await PrivateApiCall.post(`/admin/menu-imports/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function createMenuImportFromUrls(ruuid, fileUrls) {
  return await PrivateApiCall.post(`/admin/menu-imports/${ruuid}`, { fileUrls })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function createMenuImportFromWebsite(ruuid, websiteUrl) {
  return await PrivateApiCall.post(`/admin/menu-imports/${ruuid}`, { websiteUrl })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function patchMenuImportDraft(ruuid, importUuid, extractedDraft) {
  return await PrivateApiCall.patch(`/admin/menu-imports/${ruuid}/${importUuid}`, {
    extractedDraft,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** Large menu commits can run for several minutes — avoid default proxy/client cutoffs. */
const MENU_IMPORT_CONFIRM_TIMEOUT_MS = 5 * 60 * 1000;

async function confirmMenuImport(ruuid, importUuid, body = {}) {
  return await PrivateApiCall.post(
    `/admin/menu-imports/${ruuid}/${importUuid}/confirm`,
    body,
    { timeout: MENU_IMPORT_CONFIRM_TIMEOUT_MS }
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function rejectMenuImport(ruuid, importUuid) {
  return await PrivateApiCall.post(`/admin/menu-imports/${ruuid}/${importUuid}/reject`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteMenuImport(ruuid, importUuid) {
  return await PrivateApiCall.delete(`/admin/menu-imports/${ruuid}/${importUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** AI assistant — one chat step: `{ history, message, context }` or `{ history, decision, context }`. */
const AI_AGENT_TIMEOUT_MS = 3 * 60 * 1000;

/** Header search: the open restaurant's records in every area the user may open, grouped by the page listing them. */
async function globalSearch(ruuid, query) {
  return await PrivateApiCall.get(`/admin/global-search/${ruuid}?q=${encodeURIComponent(query)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** The search palette opened: the backend starts loading every page's list so the first search finds them ready. */
async function globalSearchWarm(ruuid) {
  return await PrivateApiCall.get(`/admin/global-search/${ruuid}/warm`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function aiAgentChat(ruuid, body) {
  return await PrivateApiCall.post(`/admin/ai-agent/${ruuid}/chat`, body, {
    timeout: AI_AGENT_TIMEOUT_MS,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** Persist voice-call transcript bubbles for CRM viewing. */
async function aiAgentVoiceTranscript(ruuid, body) {
  return await PrivateApiCall.post(`/admin/ai-agent/${ruuid}/conversation/voice-transcript`, body)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** Start a voice call: returns `{ callId, ticket, socketPath, model, refusal }` — the ticket opens the server's relay socket. */
async function aiAgentVoiceCall(ruuid, body) {
  return await PrivateApiCall.post(`/admin/ai-agent/${ruuid}/voice/call`, body)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** Run a tool call from the voice session (`action: 'run'`) or save an approved change (`'approve'`). */
async function aiAgentVoiceTool(ruuid, body) {
  return await PrivateApiCall.post(`/admin/ai-agent/${ruuid}/voice/tool`, body, {
    timeout: AI_AGENT_TIMEOUT_MS,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** Report a call whose relay socket dropped or could not open (WebSocket close code / reason) to the server logs. */
async function aiAgentVoiceEvent(ruuid, body) {
  return await PrivateApiCall.post(`/admin/ai-agent/${ruuid}/voice/event`, body)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************** GLOBAL CONFIG ************************************** */
/** ******************************************************************************************* */

async function getGlobalConfig() {
  return await PublicApiCall.get("/globalconfig")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function uploadFile(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post("/admin/upload/file", formData)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function uploadImage(data, doNotCompress) {
  if (!doNotCompress)
    return new Promise((resolve, reject) => {
      new Compressor(data, {
        quality: 0.2,
        success(result) {
          // const compressedFile = Object.assign(result, {
          //   path: data.name,
          //   preview: URL.createObjectURL(result)
          // });
          const formData = new FormData();
          formData.append('image', result);
          // Now upload the compressed file
          PrivateApiCall.post("/admin/upload/image", formData)
            .then(response => resolve(response))
            .catch(error => reject(error));
        },
        error(err) {
          console.error(err.message);
          reject(err);
        },
      });
    });
  else {
    const formData = new FormData();
    formData.append('image', data);
    return await PrivateApiCall.post("/admin/upload/image", formData)
      .then(response => response)
      .catch(error => errorCatch(error));
  }
}

async function uploadGlb(data) {
  const formData = new FormData();
  formData.append('glb', data);
  return await PrivateApiCall.post("/admin/upload/glb", formData)
    .then(response => response)
    .catch(error => errorCatch(error));
}

async function bulkUploadImages(images, doNotCompress = false) {
  if (!Array.isArray(images) || images.length === 0 || images.length > 50) {
    throw new Error('Please provide an array of 1-50 images');
  }

  const processedImages = await Promise.all(
    images.map(image => {
      if (doNotCompress) {
        return Promise.resolve(image);
      }

      return new Promise((resolve, reject) => {
        new Compressor(image, {
          quality: 0.2,
          success(result) {
            const fileName = image.name || 'compressed-image.jpg';
            const file = new File([result], fileName, {
              type: result.type,
              lastModified: Date.now()
            });
            resolve(file);
          },
          error(err) {
            console.error('Compression error:', err.message);
            reject(err);
          },
        });
      });
    })
  );

  const formData = new FormData();
  await processedImages.forEach(image => {
    formData.append(`image`, image);
  });

  try {
    const response = await PrivateApiCall.post("/admin/upload/image/bulk", formData);
    return response;
  } catch (error) {
    return errorCatch(error);
  }
}


async function uploadQr(data) {
  return await PrivateApiCall.post("/admin/upload/qr", data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************* TRANSLATIONS **************************************** */
/** ******************************************************************************************* */
async function getTranslations() {
  return await PublicApiCall.get("/language?platform=resto-admin")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getTranslationPages() {
  return await PrivateApiCall.get("/admin/translations/pages")
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getMissingTranslations(pageName, ruuid, limit, page) {
  let url = `/admin/translations`
  if (pageName)
    url += `/${pageName}`
  if (ruuid)
    url += `/${ruuid}`
  url += `?limit=${limit}&page=${page}`
  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function editTranslations(data) {
  return await PrivateApiCall.put(`/admin/translations/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function aiTranslate(data) {
  return await PrivateApiCall.put(`/admin/translations/generate-translation`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function aiGenerateDescription(data) {
  return await PrivateApiCall.put(`/admin/product/generate-productdescription`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getSupportedLanguagesSettings() {
  return await PrivateApiCall.get(`/admin/supported-languages/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function editSupportedLanguagesSettings(data) {
  return await PrivateApiCall.put(`/admin/supported-languages/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getSupportedLanguages() {
  return await PrivateApiCall.get(`/language/admin`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getAutomaticTranslationState() {
  return await PrivateApiCall.get(`/admin/general-config/auto-translate-setup`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function setAutomaticTranslationState(state) {
  return await PrivateApiCall.put(`/admin/general-config/auto-translate-setup`, state)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
/** ******************************************************************************************* */
/** ************************************** DELIVERY ZONE ************************************** */
/** ******************************************************************************************* */
async function getDeliveryZones(ruuid, bruuid, query) {
  return await PrivateApiCall.get(`/admin/delivery-zone${ruuid ? `/${ruuid}` : ""}${bruuid ? `/${bruuid}` : ""}${query ? `${query}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getDeliveryZone(ruuid, bruuid, uuid) {
  return await PrivateApiCall.get(
    `/admin/delivery-zone/${ruuid}/${bruuid}/${uuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addDeliveryZone(ruuid, bruuid, data) {
  return await PrivateApiCall.post(
    `/admin/delivery-zone/${ruuid}/${bruuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editDeliveryZone(ruuid, bruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/delivery-zone/${ruuid}/${bruuid}/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteDeliveryZone(ruuid, bruuid, uuid) {
  return await PrivateApiCall.delete(
    `/admin/delivery-zone/${ruuid}/${bruuid}/${uuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getOnlineDeliverySettings(ruuid, bruuid) {
  return await PrivateApiCall.get(
    `/admin/online-delivery-setting/${ruuid}/${bruuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getAllOnlineDeliverySettings(query) {
  return await PrivateApiCall.get(
    `/admin/online-delivery-setting/` + `${query ? `${query}` : ''}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditDeliverySettings(ruuid, bruuid, data) {
  return await PrivateApiCall.post(
    `/admin/online-delivery-setting/${ruuid}/${bruuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************* AI CHATBOT SETTING *********************************** */
/** ******************************************************************************************* */
async function getAllAiChatbotSettings(ruuid) {
  return await PrivateApiCall.get(`/admin/ai-chatbot-setting/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getAiChatbotSettings(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/ai-chatbot-setting/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditAiChatbotSettings(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/ai-chatbot-setting/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ********************************* AI CHATBOT CONVERSATION ********************************* */
/** ******************************************************************************************* */
// params: { pageNumber, pageLimit, search, operationType, from, to } — empty values are dropped.
async function getAiChatbotConversations(ruuid, bruuid, params) {
  const query = Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== ''))
  return await PrivateApiCall.get(`/admin/ai-chatbot-conversation/${ruuid}/${bruuid}`, { params: query })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getAiChatbotConversation(ruuid, bruuid, uuid) {
  return await PrivateApiCall.get(`/admin/ai-chatbot-conversation/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ***************************************** EVENTS ***************************************** */
/** ******************************************************************************************* */
async function getEvents(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/events/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getEvent(ruuid, bruuid, uuid) {
  return await PrivateApiCall.get(`/admin/events/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addEvent(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/events/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editEvent(ruuid, bruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/events/${ruuid}/${bruuid}/${uuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteEvent(ruuid, bruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/events/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ****************************************** MEDIA ****************************************** */
/** ******************************************************************************************* */
async function getAllMedia(ruuid, page, limit) {
  return await PrivateApiCall.get(
    page && limit
      ? `/admin/media/${ruuid}?page=${page}&limit=${limit}`
      : `/admin/media/${ruuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getAllMediaInteractions(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/media/${ruuid}/${uuid}/get-all`);
}
async function addMedia(ruuid, data) {
  return await PrivateApiCall.post(`/admin/media/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editMedia(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/media/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteMedia(ruuid, uuid) {
  return await PrivateApiCall.put(`/admin/media/${ruuid}/${uuid}/delete-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function hideMedia(ruuid, uuid) {
  return await PrivateApiCall.put(`/admin/media/${ruuid}/${uuid}/hide-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function showMedia(ruuid, uuid) {
  return await PrivateApiCall.put(`/admin/media/${ruuid}/${uuid}/show-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function sortMedia(ruuid, data) {
  return await PrivateApiCall.put(`/admin/media/${ruuid}/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateMedia(ruuid, uuid, data) {
  return await PrivateApiCall.post(`/admin/upload/${ruuid}/media/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ************************************ BACK SCREEN MEDIA ************************************* */
/** ******************************************************************************************* */
// Per restaurant + per branch, mirroring the `/admin/media` shape above. Gated server-side by
// dedicated `backScreenMedia-*` permissions or the existing `media-*` (App Media) ones.
async function getAllBackScreenMedia(ruuid, branchUuid) {
  return await PrivateApiCall.get(`/admin/back-screen-media/${ruuid}/${branchUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addBackScreenMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.post(`/admin/back-screen-media/${ruuid}/${branchUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteBackScreenMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/back-screen-media/${ruuid}/${branchUuid}/${uuid}/delete-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function toggleBackScreenMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/back-screen-media/${ruuid}/${branchUuid}/${uuid}/toggle-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function sortBackScreenMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/back-screen-media/${ruuid}/${branchUuid}/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
// Bulk, like sortBackScreenMedia above — data: { media: [{ uuid, fit }, ...] } — one call updates
// every changed item's fit instead of a round trip per image.
async function updateBackScreenMediaFit(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/back-screen-media/${ruuid}/${branchUuid}/fit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** *********************************** SELF ORDERING MEDIA ************************************ */
/** ******************************************************************************************* */
// Branch-level welcome-screen gallery + banner pick — same shape as Back Screen Media above.
async function getAllSelfOrderingMedia(ruuid, branchUuid) {
  return await PrivateApiCall.get(`/admin/self-ordering-media/${ruuid}/${branchUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addSelfOrderingMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.post(`/admin/self-ordering-media/${ruuid}/${branchUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteSelfOrderingMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/${uuid}/delete-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function toggleSelfOrderingMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/${uuid}/toggle-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function sortSelfOrderingMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
// Bulk, like sortSelfOrderingMedia above — data: { media: [{ uuid, fit }, ...] }.
async function updateSelfOrderingMediaFit(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/fit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
// Bulk, like the fit endpoint above — data: { media: [{ uuid, textOverlay }, ...] }.
async function updateSelfOrderingMediaTextOverlay(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/text-overlay`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editSelfOrderingBannerSettings(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-media/${ruuid}/${branchUuid}/banner-settings`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** ********************************* SELF ORDERING MARKETING ********************************** */
/** ******************************************************************************************* */
// Branch-level inactivity "attract screen" ads — same shape as Self Ordering Media above, plus a
// settings resource ({ isEnabled, inactivityTimeoutSeconds, buttonLabel }) instead of a banner pick.
async function getAllSelfOrderingMarketing(ruuid, branchUuid) {
  return await PrivateApiCall.get(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addSelfOrderingMarketingMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.post(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteSelfOrderingMarketingMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}/${uuid}/delete-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function toggleSelfOrderingMarketingMedia(ruuid, branchUuid, uuid) {
  return await PrivateApiCall.put(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}/${uuid}/toggle-media`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function sortSelfOrderingMarketingMedia(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateSelfOrderingMarketingMediaFit(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}/fit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editSelfOrderingMarketingSettings(ruuid, branchUuid, data) {
  return await PrivateApiCall.put(`/admin/self-ordering-marketing/${ruuid}/${branchUuid}/settings`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** **************************************** GALLERY ****************************************** */
/** ******************************************************************************************* */
async function getGallery(ruuid) {
  return await PrivateApiCall.get(`/admin/gallery/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function uploadGallery(ruuid, catuuid, data) {
  return await PrivateApiCall.post(
    `/admin/gallery/${ruuid}/${catuuid}/upload`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function updateLayout(ruuid, data) {
  return await PrivateApiCall.put(`/admin/gallery/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteGallery(ruuid, data) {
  return await PrivateApiCall.post(
    `/admin/gallery/${ruuid}/delete-photos`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getGalleryCategories(ruuid) {
  return await PrivateApiCall.get(`/admin/gallery/${ruuid}/gallery-categories`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getGalleryCategory(ruuid, catuuid) {
  return await PrivateApiCall.get(
    `/admin/gallery/${ruuid}/${catuuid}/gallery-categories`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addGalleryCategory(ruuid, data) {
  return await PrivateApiCall.post(
    `/admin/gallery/${ruuid}/gallery-categories`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editGalleryCategory(ruuid, catuuid, data) {
  return await PrivateApiCall.put(
    `/admin/gallery/${ruuid}/${catuuid}/gallery-categories`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteGalleryCategory(ruuid, catuuid) {
  return await PrivateApiCall.delete(
    `/admin/gallery/${ruuid}/${catuuid}/gallery-categories`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** **************************************** FEEDBACK ***************************************** */
/** ******************************************************************************************* */
async function getFeedbackTemplate(ruuid) {
  return await PrivateApiCall.get(`/admin/feedback-template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editFeedbackTemplate(ruuid, data) {
  return await PrivateApiCall.put(`/admin/feedback-template/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editFeedbackTemplateMessage(ruuid, data) {
  return await PrivateApiCall.put(
    `/admin/feedback-templateMessage/${ruuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getFeedback(ruuid) {
  return await PrivateApiCall.get(`/admin/feedback/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addFeedback(ruuid, data) {
  return await PrivateApiCall.post(`/admin/feedback/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editFeedbackComplaint(ruuid, fuuid, data) {
  return await PrivateApiCall.put(`/admin/feedback/${ruuid}/${fuuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** **************************************** MODULES ***************************************** */
/** ******************************************************************************************* */
async function getModules() {
  return await PrivateApiCall.get(`/organisation/modules`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** **************************************** PLATFORMS ***************************************** */
/** ******************************************************************************************* */
async function getPlatforms(ruuid) {
  return await PrivateApiCall.get(`/admin/platform/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addPlatform(ruuid, data) {
  return await PrivateApiCall.post(`/admin/platform/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editPlatform(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/platform/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deletePlatform(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/platform/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ****************************************************************************************** */
/** ************************************** PROMOTIONS **************************************** */
/** ****************************************************************************************** */
async function getAllPromotions(ruuid) {
  return await PrivateApiCall.get(`/admin/promotion/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getPromotion(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/promotion/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addPromotion(ruuid, data) {
  return await PrivateApiCall.post(`/admin/promotion/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editPromotion(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/promotion/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deletePromotion(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/promotion/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ****************************************************************************************** */
/** ************************************** BRANCH-FINANCE **************************************** */
/** ****************************************************************************************** */
async function getBranchesFinance(ruuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}/branch-finances`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateBranchesFinanceAvailability(ruuid, data) {
  return await PrivateApiCall.put(
    `/admin/product/${ruuid}/branch-finance-availability`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateBranchesFinancePrice(ruuid, data) {
  return await PrivateApiCall.put(
    `/admin/product/${ruuid}/branch-finance-price`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getBranchesFinanceProduct(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/product/${ruuid}/${uuid}/branch-finances`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ****************************************************************************************** */
/** ************************************** LOYALTY ******************************************* */
/** ****************************************************************************************** */
async function getLoyalty(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditLoyalty(ruuid, data) {
  return await PrivateApiCall.put(`/admin/loyality/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditLoyaltyDescription(ruuid, data) {
  return await PrivateApiCall.put(`/admin/loyality/${ruuid}/description`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getLoyaltyDescription(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality/${ruuid}/description`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getLoyaltyUsers(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality-user/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addLoyaltyConfig(ruuid, data) {
  return await PrivateApiCall.put(`/admin/general-config/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getLoyaltyConfig(ruuid) {
  return await PrivateApiCall.get(`/admin/general-config/${ruuid ? ruuid : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getLoyaltyMenu(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality-rewards/${ruuid}/menu`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditLoyaltyMenu(ruuid, data) {
  return await PrivateApiCall.put(`/admin/loyality-rewards/${ruuid}/menu`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getCashback(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality-rewards/${ruuid}/CASHBACK`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getGifts(ruuid) {
  return await PrivateApiCall.get(`/admin/loyality-rewards/${ruuid}/gifts`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getGift(ruuid, uuid) {
  return await PrivateApiCall.get(
    `/admin/loyality-rewards/${ruuid}/${uuid}/gifts`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addEditCashback(ruuid, data) {
  return await PrivateApiCall.put(
    `/admin/loyality-rewards/${ruuid}/cashback`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editGift(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/loyality-rewards/${ruuid}/${uuid}/gifts`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addGift(ruuid, data) {
  return await PrivateApiCall.post(
    `/admin/loyality-rewards/${ruuid}/gifts`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteGift(ruuid, uuid) {
  return await PrivateApiCall.delete(
    `/admin/loyality-rewards/${ruuid}/${uuid}/gifts`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getRewardsBasedOnLevel(ruuid) {
  return await PrivateApiCall.get(
    `/admin/loyality-rewards/${ruuid}/reward-based-onlevel`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getRewardBasedOnLevel(ruuid, uuid) {
  return await PrivateApiCall.get(
    `/admin/loyality-rewards/${ruuid}/${uuid}/reward-based-onlevel`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteRewardBasedOnLevel(ruuid, uuid) {
  return await PrivateApiCall.delete(
    `/admin/loyality-rewards/${ruuid}/${uuid}/reward-based-onlevel`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editRewardBasedOnLevel(ruuid, uuid, data) {
  return await PrivateApiCall.put(
    `/admin/loyality-rewards/${ruuid}/${uuid}/reward-based-onlevel`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addRewardBasedOnLevel(ruuid, data) {
  return await PrivateApiCall.post(
    `/admin/loyality-rewards/${ruuid}/reward-based-onlevel`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editLoyaltyPoints(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/loyality-user/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getLoyaltyUserEntries(ruuid, uuid, fromDate) {
  return await PrivateApiCall.get(ruuid ? `/admin/loyality-user/entries/${ruuid}/${uuid}?fromDate=${fromDate}` : `/admin/loyality-user/entries/${uuid}?fromDate=${fromDate}`)
    .then((response) => response)
    .catch((error) => errorCatch((error)))
}
/** ****************************************************************************************** */
/** ************************************** DISCOUNT ******************************************* */
/** ****************************************************************************************** */
async function getAllDiscount(ruuid) {
  return await PrivateApiCall.get(`/admin/discount/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function getDiscount(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/discount/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function addDiscount(ruuid, data) {
  return await PrivateApiCall.post(`/admin/discount/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function editDiscount(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/discount/${ruuid}/${uuid}/edit`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function deleteDiscount(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/discount/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function sortDiscount(ruuid, data) {
  return await PrivateApiCall.put(`/admin/discount/${ruuid}/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function editDiscountSettings(ruuid, data) {
  return await PrivateApiCall.put(`/admin/discount/${ruuid}/settings`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDiscountSettings(ruuid) {
  return await PrivateApiCall.get(`/admin/discount/${ruuid}/settings`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ***************************************** ORDERS ****************************************** */
/** ******************************************************************************************* */

async function getOrders(ruuid, buuid, page, limit, filterUrl, cancelToken) {
  return await PrivateApiCall.get(`/admin/order/paginated` + (ruuid ? `/${ruuid}` : '') + (buuid ? `/${buuid}` : '') + `?page=${page}&limit=${limit}${filterUrl ? `&${filterUrl}` : ""}`, {
    cancelToken: cancelToken,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function checkIfOrderMeetsFilters(ruuid, buuid, ouuid, filterUrl) {
  return await PrivateApiCall.get(`/admin/order/check-if-in-filters/${ruuid}/${buuid}/${ouuid}${filterUrl ? `?${filterUrl}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

//   return await PrivateApiCall.get(`/admin/order/paginated/${ruuid}/${buuid}?page=${page}&limit=${limit}${filter}`)
//     .then((response) => response)
//     .catch((error) => errorCatch(error));
// }

async function getOrder(ruuid, buuid, ouuid, options = {}) {
  const qs = options?.isRefund ? '?isRefund=1' : ''
  return await PrivateApiCall.get(`/admin/order/${ruuid}/${buuid}/${ouuid}${qs}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getOrderActivityHistory(ruuid, buuid, ouuid) {
  return await PrivateApiCall.get(`/admin/order/${ruuid}/${buuid}/${ouuid}/activity-history`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function cancelOrder(ruuid, buuid, ouuid, wasOrderAccepted = false, data) {
  return await PrivateApiCall.put(
    `/admin/order/${ruuid}/${buuid}/${ouuid}/${wasOrderAccepted ? "cancel-accepted-order" : "cancel-order"}`, data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function acceptOrder(ruuid, buuid, ouuid) {
  return await PrivateApiCall.put(
    `/admin/order/${ruuid}/${buuid}/${ouuid}/accept-order`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function rejectOrder(ruuid, buuid, ouuid, wasOrderAccepted = false, data) {
  return await PrivateApiCall.put(
    `/admin/order/${ruuid}/${buuid}/${ouuid}/${wasOrderAccepted ? "reject-accepted-order" : "reject-order"}`, data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function completeOrder(ruuid, buuid, ouuid) {
  return await PrivateApiCall.put(
    `/admin/order/${ruuid}/${buuid}/${ouuid}/complete-order`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function dispatchOrder(ruuid, bruuid, data) {
  return await PrivateApiCall.put(`/admin/order/${ruuid}/${bruuid}/dispatch-order`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateDispatchSettings(ruuid, data) {
  return await PrivateApiCall.put(`/admin/general-config/dispatch-setup/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDrivers(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/order/get-drivers${ruuid ? `/${ruuid}` : ""}${bruuid ? `/${bruuid}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDriver(uuid, query) {
  return await PrivateApiCall.get(`/admin/order/get-driver-details/${uuid}${query ? `?${query}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function undispatchAllOrders(ruuid, bruuid, duuid, filterUrl) {
  return await PrivateApiCall.put(`/admin/order${ruuid ? `/${ruuid}` : ""}${bruuid ? `/${bruuid}` : ""}/undispatch-orders/${duuid}${filterUrl ? `${filterUrl}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function undispatchOrder(ruuid, bruuid, duuid, ouuid) {
  return await PrivateApiCall.put(`/admin/order/${ruuid}/${bruuid}/undispatch-order/${duuid}/${ouuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setBusy(ruuid, bruuid, data, query) {
  return await PrivateApiCall.put(`/admin/order/${ruuid}/${bruuid}/set-busy` + `${query ? query : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setBusyAll(data, query) {
  return await PrivateApiCall.put(`/admin/order/set-busy-all` + `${query ? query : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getUnclosedDays(ruuid, bruuid, query) {
  return await PrivateApiCall.get(`/admin/report${ruuid ? `${ruuid}` : ''}${bruuid ? `${bruuid}` : ''}/unclosed-dates${query ? `${query}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function closeDay(ruuid, bruuid, closingDay) {
  return await PrivateApiCall.post(`/admin/report/close-day/${ruuid}/${bruuid}`, closingDay)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setClosed(ruuid, bruuid, data, query) {
  return await PrivateApiCall.put(`/admin/order/${ruuid}/${bruuid}/set-close` + `${query ? query : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setClosedAll(data, query) {
  return await PrivateApiCall.put(`/admin/order/set-close-all` + `${query ? query : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTodayReport(ruuid, bruuid, selectedDate, query) {
  return await PrivateApiCall.get(`/admin/report${ruuid ? `${ruuid}` : ''}${bruuid ? `${bruuid}` : ''}/reports?key=not-closed${query ? `${query}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getClosedReport(ruuid, bruuid, selectedDate) {
  return await PrivateApiCall.get(`/admin/report/${ruuid}/${bruuid}/reports?selectedDate=${selectedDate}&key=closed`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getReportDates(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/report/${ruuid}/${bruuid}/report-dates`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getReasons() {
  return await PrivateApiCall.get(`/admin/reasons`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addReason(data) {
  return await PrivateApiCall.post(`/admin/reasons`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editReason(uuid, data) {
  return await PrivateApiCall.put(`/admin/reasons/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteReason(uuid) {
  return await PrivateApiCall.delete(`/admin/reasons/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** ORDERS HISTORY ************************************* */
/** ******************************************************************************************* */
// async function getOrdersHistory(ruuid, buuid, page, limit, filterUrl) {
//   return await PrivateApiCall.get(`/admin/order/history/${ruuid}/${buuid}?page=${1}&limit=${limit}${filterUrl ? filterUrl : ""}`)
//     .then((response) => response)
//     .catch((error) => errorCatch(error));
// }

async function getOrdersHistory(ruuid, buuid, page, limit, filterUrl, cancelToken) {
  return await PrivateApiCall.get(`/admin/order/history` + (ruuid ? `/${ruuid}` : '') + (buuid ? `/${buuid}` : '') + `?page=${page}&limit=${limit}${filterUrl ? filterUrl : ""}`, {
    cancelToken: cancelToken,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ***************************************** TABLE ORDERS ****************************************** */
/** ******************************************************************************************* */

async function getTableOrders(ruuid, buuid, page, limit, filterUrl, cancelToken) {
  return await PrivateApiCall.get(`/admin/order/table-ordering/paginated` + (ruuid ? `/${ruuid}` : '') + (buuid ? `/${buuid}` : '') + `?page=${page}&limit=${limit}${filterUrl ? `&${filterUrl}` : ""}`, {
    cancelToken: cancelToken,
  })
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function updateTableOrderStatus(ruuid, buuid, ouuid, data) {
  return await PrivateApiCall.put(
    `/admin/order/table-ordering/${ruuid}/${buuid}/${ouuid}/update-status`, data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function payTableOrder(ruuid, buuid, ouuid, data) {
  return await PrivateApiCall.put(
    `/admin/order/table-ordering/${ruuid}/${buuid}/${ouuid}/pay-order`, data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** *************************************** TABLE ACTIONS ************************************* */
/** ******************************************************************************************* */

async function getTableActions(filterUrl) {
  return await PrivateApiCall.get(`/admin/order/qr-button-actions${filterUrl ? `?${filterUrl}` : ""}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** *****************************************  FLOOR SETUP  ****************************************** */
/** ******************************************************************************************* */

async function getFloors(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/floor/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getFloor(ruuid, bruuid, fuuid) {
  return await PrivateApiCall.get(`/admin/floor/${ruuid}/${bruuid}/${fuuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTables(fuuid) {
  return await PrivateApiCall.get(`/admin/table/${fuuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getLastTableNumberInBranch(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/table/${ruuid}/${bruuid}/last-number`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editOrAddFloorsAndTables(ruuid, bruuid, data) {
  return await PrivateApiCall.put(`/admin/floor/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateFloorOrder(ruuid, bruuid, floorUuids) {
  return await PrivateApiCall.patch(`/admin/floor/${ruuid}/${bruuid}/order`, { floorUuids })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteTheFloor(ruuid, bruuid, fuuid) {
  return await PrivateApiCall.delete(`/admin/floor/${ruuid}/${bruuid}/${fuuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteTable(fuuid, tuuid) {
  return await PrivateApiCall.delete(`/admin/table/${fuuid}/${tuuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *****************************************  QR  ****************************************** */
/** ******************************************************************************************* */

async function addQR(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/qr-setup/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addQrTables(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/qr-tables/${ruuid}/${bruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addQrButton(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/qr-button/${ruuid}/${bruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editQrButton(ruuid, bruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/qr/qr-button/${ruuid}/${bruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//bulk add qr buttons
async function bulkAddQrButtons(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/qr-buttons/${ruuid}/${bruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//bulk update qr buttons
async function bulkEditQrButtons(ruuid, bruuid, data) {
  return await PrivateApiCall.put(`/admin/qr/qr-buttons/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteQrButton(ruuid, bruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/qr/qr-button/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteQrTable(ruuid, bruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/qr/qr-tables/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getQrButton(ruuid, bruuid, uuid) {
  return await PrivateApiCall.get(`/admin/qr/qr-button/${ruuid}/${bruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getQrButtons(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/qr/qr-button/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getQr(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/qr/qr-setup/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function validateURL(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/url-validation/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getQrTables(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/qr/qr-tables/${ruuid}/${bruuid}/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getTableOrderingSettings(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/table-ordering-setting/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function addEditTableOrderingSettings(ruuid, bruuid, data) {
  return await PrivateApiCall.post(`/admin/table-ordering-setting/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getQrOnlineDelivery(ruuid) {
  return await PrivateApiCall.get(`/admin/qr/online-ordering/${ruuid}/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function addOnlineDeliveryQr(ruuid, data) {
  return await PrivateApiCall.post(`/admin/qr/online-ordering/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getQROrderingSettings(ruuid) {
  return await PrivateApiCall.get(
    `/admin/general-config/get-order-config/${ruuid}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
async function setQROrderingSettings(ruuid, data) {
  return await PrivateApiCall.post(
    `/admin/general-config/set-order-config/${ruuid}`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** *************************************** ENTITY QR  **************************************** */
/** ******************************************************************************************* */
//entity qr
async function getEntityQr(euuid) {
  return await PrivateApiCall.get(`/admin/qr/entity-ordering/qr-setup/${euuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addEditEntityQr(euuid, data) {
  return await PrivateApiCall.post(`/admin/qr/entity-ordering/qr-setup/${euuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//entity qr tables
async function getEntityQrTables(euuid) {
  return await PrivateApiCall.get(`/admin/qr/entity-ordering/qr-tables/${euuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addEntityQrTables(euuid, data) {
  return await PrivateApiCall.post(`/admin/qr/entity-ordering/qr-tables/${euuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteEntityQrTable(euuid, uuid) {
  return await PrivateApiCall.delete(`/admin/qr/entity-ordering/qr-tables/${euuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//bulk add entity qr buttons
async function bulkAddEntityQrButtons(euuid, data) {
  return await PrivateApiCall.post(`/admin/qr/entity-ordering/qr-buttons/${euuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//bulk update entity qr buttons
async function bulkUpdateEntityQrButtons(euuid, data) {
  return await PrivateApiCall.put(`/admin/qr/entity-ordering/qr-buttons/${euuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//entity qr buttons
async function getEntityQrButton(euuid, uuid) {
  return await PrivateApiCall.get(`/admin/qr/entity-ordering/qr-button/${euuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getEntityQrButtons(euuid) {
  return await PrivateApiCall.get(`/admin/qr/entity-ordering/qr-button/${euuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addEntityQrButton(euuid, data) {
  return await PrivateApiCall.post(`/admin/qr/entity-ordering/qr-button/${euuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editEntityQrButton(euuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/qr/entity-ordering/qr-button/${euuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteEntityQrButton(euuid, uuid) {
  return await PrivateApiCall.delete(`/admin/qr/entity-ordering/qr-button/${euuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
/** ******************************************************************************************* */
/** *************************************  URL LINK  ****************************************** */
/** ******************************************************************************************* */
async function getAffiliateLink(ruuid) {
  return await PrivateApiCall.get(`/admin/affiliate-link/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addAffiliateLink(ruuid, data) {
  return await PrivateApiCall.post(`/admin/affiliate-link/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editAffiliateLink(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/affiliate-link/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function deleteAffiliateLink(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/affiliate-link/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}
/** ******************************************************************************************* */
/** *************************************  DESIGN SETUP  ************************************** */
/** ******************************************************************************************* */

async function getDesignSetupGlobalSettings() {
  return await PrivateApiCall.get(`/admin/general-config/design-setup`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editDesignSetupGlobalSettings(data) {
  return await PrivateApiCall.put(`/admin/general-config/design-setup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDesignSetup(ruuid) {
  return await PrivateApiCall.get(`/admin/design-setup/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editDesignSetup(ruuid, data) {
  return await PrivateApiCall.post(`/admin/design-setup/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// Custom design templates: saved by the admin from the Themes page, shared by all the account's restaurants.
async function getDesignTemplates() {
  return await PrivateApiCall.get(`/admin/design-setup-templates`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function createDesignTemplate(data) {
  return await PrivateApiCall.post(`/admin/design-setup-templates`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteDesignTemplate(uuid) {
  return await PrivateApiCall.delete(`/admin/design-setup-templates/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** **************************************  LOGIN SETUP  ************************************** */
/** ******************************************************************************************* */

async function getLoginDesignSetup() {
  return await PrivateApiCall.get(`/admin/loginDesignSetup`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editLoginDesignSetup(data) {
  return await PrivateApiCall.put(`/admin/loginDesignSetup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosLoginDesignSetup(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/posLoginDesignSetup/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editPosLoginDesignSetup(ruuid, buuid, data) {
  return await PrivateApiCall.put(`/admin/posLoginDesignSetup/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** **********************************  SPLASH SCREEN SETUP  ********************************** */
/** ******************************************************************************************* */

async function getSplashScreenDesignSetup() {
  return await PrivateApiCall.get(`/admin/splashScreen`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editSplashScreenDesignSetup(data) {
  return await PrivateApiCall.put(`/admin/splashScreen`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ********************************  RESTAURANTS DESIGN SETUP  ******************************* */
/** ******************************************************************************************* */

async function getRestaurantsDesignSetup() {
  return await PrivateApiCall.get(`/admin/restaurantsDesignSetup`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editRestaurantsDesignSetup(data) {
  return await PrivateApiCall.put(`/admin/restaurantsDesignSetup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************  PRODUCTS AVAILABILITY  ********************************* */
/** ******************************************************************************************* */

async function getProductsAvailability(ruuid, bruuid) {
  return await PrivateApiCall.get(`/admin/product-availability/${ruuid}/${bruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteProductAvailability(ruuid, bruuid, pruuid) {
  return await PrivateApiCall.delete(`/admin/product-availability/${ruuid}/${bruuid}/${pruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateProductsAvailability(ruuid, bruuid, data) {
  return await PrivateApiCall.put(`/admin/product-availability/${ruuid}/${bruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************  AUTOMATED-MESSAGES  ********************************* */
/** ******************************************************************************************* */

async function getAutomatedMessages(ruuid) {
  return await PrivateApiCall.get(`/admin/automatedMessage/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAutomatedMessage(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/automatedMessage/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAutomatedJobs(ruuid, uuid, query) {
  return await PrivateApiCall.get(query ? `/admin/automatedMessage/${ruuid}/${uuid}/jobs?${query}` : `/admin/automatedMessage/${ruuid}/${uuid}/jobs`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addAutomatedMessage(ruuid, data) {
  return await PrivateApiCall.post(`/admin/automatedMessage/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editAutomatedMessage(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/automatedMessage/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteAutomatedMessage(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/automatedMessage/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************  DIRECT-MESSAGES  ********************************* */
/** ******************************************************************************************* */

async function getDirectMessages(ruuid) {
  return await PrivateApiCall.get(`/admin/directMessage/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDirectMessage(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/directMessage/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomersBasedOnCriteria(ruuid, criteria) {
  return await PrivateApiCall.post(`/admin/directMessage/criteria/customers/${ruuid}`, criteria)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addDirectMessage(ruuid, data) {
  return await PrivateApiCall.post(`/admin/directMessage/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editDirectMessage(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/directMessage/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteDirectMessage(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/directMessage/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDirectMessagesWithCustomers(ruuid) {
  return await PrivateApiCall.get(`/admin/directMessage/all/customers/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDirectMessagesJobs(ruuid, uuid, query) {
  return await PrivateApiCall.get(query ? `/admin/directMessage/${ruuid}/${uuid}/jobs?${query}` : `/admin/directMessage/${ruuid}/${uuid}/jobs`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** **************************************  MESSAGES  ***************************************** */
/** ******************************************************************************************* */

async function getMessagesRestaurantsAndBranches() {
  return await PrivateApiCall.get(`/admin/restaurant/filtered-branches`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteChat(ruuid, bruuid, chatId) {
  return await PrivateApiCallMessaging.delete(`/${ruuid}/${bruuid}/${chatId}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getUserChats(ruuid, bruuid) {
  return await PrivateApiCallMessaging.get(`/branch/${ruuid}/${bruuid}/all-chats`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function addUpdateChat(ruuid, bruuid, uuid, data) {
  return await PrivateApiCallMessaging.put(`/${ruuid}/${bruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function getChatMessages(ruuid, bruuid, chatId, pageLimit, page) {
  return await PrivateApiCallMessaging.get(pageLimit && page ? `/${ruuid}/${bruuid}/${chatId}?pageNumber=${page}&pageLimit=${pageLimit}` : `/${ruuid}/${bruuid}/${chatId}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function updateMessage(chatId, messageId, data) {
  return await PrivateApiCallMessaging.put(`/chat/updateMessage/${chatId}/${messageId}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
async function deleteMessage(chatId, messageId) {
  return await PrivateApiCallMessaging.delete(`/chat/deleteMessage/${chatId}/${messageId}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getUserGlobalChats() {
  return await PrivateApiCallMessaging.get(`/global/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************  NOTIFICATIONS  *************************************** */
/** ******************************************************************************************* */

async function registerDevice(token) {
  return await PrivateApiCall.post(`/admin/notifications`, { token: token })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNotifications() {
  return await PrivateApiCall.get(`/admin/notifications`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function viewNotification(uuid) {
  return await PrivateApiCall.put(`/admin/notifications/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function confirmNotification(uuid) {
  return await PrivateApiCall.put(`/admin/notifications/delivered/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************ DASHBOARD  *************************************** */
/** ******************************************************************************************* */

const reshapeUrl = (baseUrl, ruuid, filterString, selectedCurrency) => {
  let final = baseUrl
  if (ruuid !== 'all-restaurants' && ruuid) {
    final += `/${ruuid}/`
  }
  if (filterString)
    final += filterString
  if (selectedCurrency)
    final += `&currency=${selectedCurrency}`
  return final
}

async function getTopSpenders(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/top-customers`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAvgOrderPrice(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/avg-order-price', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getLateOrders(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/late-orders', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTotalOrders(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/total-orders', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNewCustomers(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/get-customer-count', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCanceledOrders(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/canceled-orders', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTotalCustomers() {
  return await PrivateApiCall.get('/admin/dashboard/total-customers')
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByZone(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/sales-by-zone', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByAffiliate() {
  return await PrivateApiCall.get(`/admin/dashboard/sales-by-affiliate`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSignupsByAffiliate() {
  return await PrivateApiCall.get(`/admin/dashboard/sign-ups-by-affiliate`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}
//NEW APIS
async function getTotalDiscounts(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/total-discounts`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTotalPromoDiscounts(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/total-promo-discounts`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTopXItemsQty(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/top-x-items`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTopXItemsAmt(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/top-x-items-amount`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNotActiveCustomers(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/not-active-customers`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getFeedbacksAndComplaints(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-feedbacks`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getFeedbacksRating(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-feebacks-rating`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTotalRevenuePerMonth(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-revenue-month`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNewProduct(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-new-product`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerCountValue(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-customer-count-above-value`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerCountOrder(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-customer-count-above-count`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getQrCount(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-qr-count`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNewProductsList(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-new-product-list`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getFeedbacksSummary(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-feedback-summary`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getBirthdayCount(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-birthday-count`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDeliveryStatus(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-delivery-status`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosSyncStatus(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-pos-sync-status`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getBusinessDaysStatus(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-business-days-status`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNotSoldItems(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/not-sold-items`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByRestaurant(filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-sales-by-restaurant`, undefined, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByBranch(filterString, selectedCurrency) {
  return await PrivateApiCall.get(`/admin/dashboard/get-sales-by-branch`, undefined, filterString, selectedCurrency)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAffiliateClicks() {
  return await PrivateApiCall.get(`/admin/dashboard/scans-by-affiliate`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getOrdersStatus(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-today-orders`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTopItemsBranch(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/top-x-items-items-by-branch`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAvgOrders(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-avg-orders`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByCategory(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-sales-by-category`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByCustomer(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-sales-by-customer`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAvgPrepTime(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-avg-prep-time`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAvgDeliveryTime(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl(`/admin/dashboard/get-avg-delivery-time`, ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRestaurantsCurrency() {
  return await PrivateApiCall.get(`/admin/dashboard/get-restaurants-currency`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getConsolidatedCurrency() {
  return await PrivateApiCall.get(`/admin/dashboard/get-consolidated-currency`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setConsolidatedCurrency(data) {
  return await PrivateApiCall.post(`/admin/dashboard/set-consolidated-currency/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCardsPositioning() {
  return await PrivateApiCall.get(`admin/dashboard/get-cards-position/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setCardsPositioning(data) {
  return await PrivateApiCall.put(`/admin/dashboard/set-cards-position/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// New (fully-customizable) sales dashboard layout — one saved layout per
// admin user, same auth/scope as get-cards-position/set-cards-position
// above. See BACKEND_NEW_DESIGN_LAYOUT_API.md for the full contract.
async function getNewDesignLayout() {
  return await PrivateApiCall.get(`/admin/dashboard/get-new-design-layout/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setNewDesignLayout(data) {
  return await PrivateApiCall.put(`/admin/dashboard/set-new-design-layout/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// New Design dashboard DATA API (as opposed to the layout API above) — one
// aggregated call replacing dozens of classic dashboard endpoints. Same
// auth (authorizationRole(GETDASHBOARD)) as the classic dashboard. `ruuid`
// goes through reshapeUrl (path segment), everything else — fromDate/toDate,
// buuid, source, currency, sections — rides in filterString/selectedCurrency.
// See BACKEND_NEW_DESIGN_DASHBOARD_API.md for the full response envelope.
async function getNewDesignDashboardData(ruuid, filterString, selectedCurrency) {
  return await PrivateApiCall.get(reshapeUrl('/admin/dashboard/new-design', ruuid, filterString, selectedCurrency))
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ EMAIL SETUP  *************************************** */
/** ******************************************************************************************* */

// `module` picks the mailbox: 'purchase-order' = the one purchase orders are emailed from;
// omitted = the general one.
const emailServerQuery = (module) => (module ? `?module=${module}` : '')

async function getEmailServer(module) {
  return await PrivateApiCall.get(`/admin/email-server${emailServerQuery(module)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addEmailServer(data, module) {
  return await PrivateApiCall.post(`/admin/email-server${emailServerQuery(module)}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// Removes a dedicated mailbox so that module goes back to the general one
async function deleteEmailServer(module) {
  return await PrivateApiCall.delete(`/admin/email-server${emailServerQuery(module)}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function testEmail(data, module) {
  return await PrivateApiCall.post(`/admin/email-server/test${emailServerQuery(module)}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ************************************************************************************** */
/** ************************************ PAYMENTS  *************************************** */
/** ************************************************************************************** */

async function addDefaultPayment(ruuid) {
  return await PrivateApiCall.post(`admin/payment/${ruuid}/auto-generate`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPayments() {
  return await PrivateApiCall.get(`/admin/payment`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPayment(uuid) {
  return await PrivateApiCall.get(`/admin/payment/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editPayment(uuid, data) {
  return await PrivateApiCall.put(`/admin/payment/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deletePayment(uuid) {
  return await PrivateApiCall.delete(`/admin/payment/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addPayment(data) {
  return await PrivateApiCall.post(`/admin/payment`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function sortPayments(ruuid, data) {
  return await PrivateApiCall.put(`/admin/payment/${ruuid}/sorting`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddPayments(data) {
  return await PrivateApiCall.post(`/admin/payment/bulk-add`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdatePayments(data) {
  return await PrivateApiCall.put(`/admin/payment/bulk-update`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updatePaymentImages(data) {
  return await PrivateApiCall.put(`/admin/payment/images`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportPaymentsExcel() {
  return await PrivateApiCall.get(`/admin/payment/export/template`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importPaymentsExcel(data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/payment/import`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPaymentBills() {
  return await PrivateApiCall.get(`/admin/payment-bill`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function hidePaymentBill(data) {
  return await PrivateApiCall.post(`/admin/payment-bill/hide`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkHidePaymentBills(data) {
  return await PrivateApiCall.post(`/admin/payment-bill/bulk-hide`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function unhidePaymentBill(uuid) {
  return await PrivateApiCall.delete(`/admin/payment-bill/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ************************************************************************************** */
/** ********************************** CURRENCIES  *************************************** */
/** ************************************************************************************** */

async function getRates(ruuid) {
  return await PrivateApiCall.get(`admin/currency-rate/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRate(ruuid, uuid) {
  return await PrivateApiCall.get(`admin/currency-rate/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editRate(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/currency-rate/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteRate(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/currency-rate/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addRate(ruuid, data) {
  return await PrivateApiCall.post(`/admin/currency-rate/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function generateRates(ruuid) {
  return await PrivateApiCall.post(`admin/currency-rate/${ruuid}/auto-generate`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkAddCurrencyRates(ruuid, data) {
  return await PrivateApiCall.post(`/admin/currency-rate/bulk-add/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateCurrencyRates(ruuid, data) {
  return await PrivateApiCall.put(`/admin/currency-rate/bulk-update/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateCurrencyImages(ruuid, data) {
  return await PrivateApiCall.put(`/admin/currency-rate/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function exportCurrencyExcel(ruuid) {
  return await PrivateApiCall.get(`/admin/currency-rate/export/template/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function importCurrencyExcel(ruuid, data) {
  const formData = new FormData();
  formData.append('file', data);
  return await PrivateApiCall.post(`/admin/currency-rate/import/${ruuid}`, formData)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

//stock functions
/** ******************************************************************************************* */
/** ************************************** Recipes  ******************************************* */
/** ******************************************************************************************* */

async function getRecipesProducts(ruuid, limit) {
  // Build query parameters, excluding undefined values
  const params = new URLSearchParams();
  if (limit !== undefined && limit !== null) params.append('limit', limit);

  const queryString = params.toString();
  const url = `admin/recipe/products/${ruuid}${queryString ? `?${queryString}` : ''}`;

  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRecipeData(ruuid, puuid, smdUuid) {
  return await PrivateApiCall.get(`admin/recipe/${ruuid}/data?productUuid=${puuid}&smdUuid=${smdUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAllRecipes(ruuid, category, page, limit, sortBy, sortOrder) {
  // Build query parameters, excluding undefined values
  const params = new URLSearchParams();
  if (category !== undefined && category !== null) params.append('category', category);
  if (page !== undefined && page !== null) params.append('page', page);
  if (limit !== undefined && limit !== null) params.append('limit', limit);
  if (sortBy !== undefined && sortBy !== null) params.append('sortBy', sortBy);
  if (sortOrder !== undefined && sortOrder !== null) params.append('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = `admin/recipe/${ruuid}${queryString ? `?${queryString}` : ''}`;

  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRecipeByCategory(ruuid, categoryName) {
  return await PrivateApiCall.get(`admin/recipe/category/${ruuid}/${categoryName}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSingleRecipe(uuid) {
  return await PrivateApiCall.get(`admin/recipe/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSingleProductRecipes(ruuid, puuid) {
  return await PrivateApiCall.get(`admin/recipe/product/${ruuid}/${puuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addRecipe(ruuid, data) {
  return await PrivateApiCall.post(`admin/recipe/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkCreateRecipes(ruuid, data) {
  return await PrivateApiCall.post(`admin/recipe/${ruuid}/bulk-create`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateRecipe(ruuid, uuid, data) {
  return await PrivateApiCall.put(`admin/recipe/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteRecipe(ruuid, uuid) {
  return await PrivateApiCall.delete(`admin/recipe/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCostAnalysis(ruuid, uuid, modules) {
  // Build query parameters, handling modules array properly
  const params = new URLSearchParams();
  if (modules && Array.isArray(modules) && modules.length > 0) {
    // Add each module as a separate parameter or join them appropriately
    // Depending on API expectation, could be: modules=mod1&modules=mod2
    modules.forEach(module => {
      if (module !== undefined && module !== null && module !== '') {
        params.append('modules', module);
      }
    });
  }

  const queryString = params.toString();
  const url = `admin/recipe/${ruuid}/${uuid}/cost-analysis${queryString ? `?${queryString}` : ''}`;

  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function applyMarkup(ruuid, uuid, data) {
  return await PrivateApiCall.post(`admin/recipe/${ruuid}/${uuid}/apply-markup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRecipeInstructions(ruuid, uuid) {
  return await PrivateApiCall.get(`admin/recipe/${ruuid}/${uuid}/instructions`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateRecipeInstructions(ruuid, uuid, data) {
  return await PrivateApiCall.put(`admin/recipe/${ruuid}/${uuid}/instructions`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function searchRecipes(ruuid, query, category, modules, page, limit) {
  // Build query parameters, excluding undefined values
  const params = new URLSearchParams();
  if (query !== undefined && query !== null) params.append('query', query);
  if (category !== undefined && category !== null) params.append('category', category);
  if (modules !== undefined && modules !== null) params.append('modules', modules);
  if (page !== undefined && page !== null) params.append('page', page);
  if (limit !== undefined && limit !== null) params.append('limit', limit);

  const queryString = params.toString();
  const url = `admin/recipe/${ruuid}/search${queryString ? `?${queryString}` : ''}`;

  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRecipeAnalysis(ruuid, category, modules, dateFrom, dateTo, page, limit) {
  // Build query parameters, excluding undefined values
  const params = new URLSearchParams();
  if (category !== undefined && category !== null) params.append('category', category);
  if (modules !== undefined && modules !== null) params.append('modules', modules);
  if (dateFrom !== undefined && dateFrom !== null) params.append('dateFrom', dateFrom);
  if (dateTo !== undefined && dateTo !== null) params.append('dateTo', dateTo);
  if (page !== undefined && page !== null) params.append('page', page);
  if (limit !== undefined && limit !== null) params.append('limit', limit);

  const queryString = params.toString();
  const url = `admin/recipe/analysis/${ruuid}${queryString ? `?${queryString}` : ''}`;

  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** UOM  ******************************************* */
/** ******************************************************************************************* */
async function getUoms() {
  return await PrivateApiCall.get(`admin/smdUom`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getConnectedUoms(itemUuid) {
  return await PrivateApiCall.get(`/admin/SmdUOM/${itemUuid}/connected`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getUom(uuid) {
  return await PrivateApiCall.get(`admin/smdUom/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addUom(data) {
  return await PrivateApiCall.post(`admin/smdUom`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editUom(uuid, data) {
  return await PrivateApiCall.put(`admin/smdUom/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteUom(uuid) {
  return await PrivateApiCall.delete(`admin/smdUom/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateUoms(data) {
  return await PrivateApiCall.put(`admin/smdUOM`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ***************************************** PUOM  ******************************************* */
/** ******************************************************************************************* */
async function getPuoms() {
  return await PrivateApiCall.get(`/admin/SmdPUOM`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPuom(uuid) {
  return await PrivateApiCall.get(`/admin/SmdPUOM/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addPuom(data) {
  return await PrivateApiCall.post(`/admin/SmdPUOM`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editPuom(uuid, data) {
  return await PrivateApiCall.put(`/admin/SmdPUOM/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deletePuom(uuid) {
  return await PrivateApiCall.delete(`/admin/SmdPUOM/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** UOM Conversions  ************************************** */
/** ******************************************************************************************* */
async function getConversions() {
  return await PrivateApiCall.get(`admin/smdUomConversion`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getConversion(uuid) {
  return await PrivateApiCall.get(`admin/smdUomConversion/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addConversion(data) {
  return await PrivateApiCall.post(`admin/smdUomConversion`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editConversion(uuid, data) {
  return await PrivateApiCall.put(`admin/smdUomConversion/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteConversion(uuid) {
  return await PrivateApiCall.delete(`admin/smdUomConversion/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateConversions(data) {
  return await PrivateApiCall.put(`admin/smdUOMConversion`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** Stock Items  ****************************************** */
/** ******************************************************************************************* */

async function getStockItems() {
  return await PrivateApiCall.get(`admin/SmdItem/all`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addStockItem(data) {
  return await PrivateApiCall.post(`admin/SmdItem`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStockItem(uuid, data) {
  return await PrivateApiCall.put(`admin/SmdItem/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** Linked Items(Purchased Items)  ************************ */
/** ******************************************************************************************* */

async function getLinkedItems() {
  return await PrivateApiCall.get(`admin/SmdLinkedItem/all`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getLinkedItem(uuid) {
  return await PrivateApiCall.get(`admin/SmdLinkedItem/all/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addLinkedItem(data) {
  return await PrivateApiCall.post(`admin/SmdLinkedItem`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editLinkedItem(uuid, data) {
  return await PrivateApiCall.put(`admin/SmdLinkedItem/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteLinkedItem(uuid) {
  return await PrivateApiCall.delete(`admin/SmdLinkedItem/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** Stock Categories  ************************************* */
/** ******************************************************************************************* */
async function getStockCategories() {
  return await PrivateApiCall.get(`admin/smdItemCategory`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getStockCategory(uuid) {
  return await PrivateApiCall.get(`admin/smdItemCategory/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addStockCategory(data) {
  return await PrivateApiCall.post(`admin/smdItemCategory`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStockCategory(uuid, data) {
  return await PrivateApiCall.put(`admin/smdItemCategory/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStockCategory(uuid) {
  return await PrivateApiCall.delete(`admin/smdItemCategory/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateStockCategories(data) {
  return await PrivateApiCall.put(`admin/smdItemCategory`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ Stock Groups  **************************************** */
/** ******************************************************************************************* */
async function getStockGroups() {
  return await PrivateApiCall.get(`admin/smdItemGroup`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getStockGroup(uuid) {
  return await PrivateApiCall.get(`admin/smdItemGroup/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addStockGroup(data) {
  return await PrivateApiCall.post(`admin/smdItemGroup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStockGroup(uuid, data) {
  return await PrivateApiCall.put(`admin/smdItemGroup/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStockGroup(uuid) {
  return await PrivateApiCall.delete(`admin/smdItemGroup/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateStockGroups(data) {
  return await PrivateApiCall.put(`admin/smdItemGroup`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** Stock Types  ************************************* */
/** ******************************************************************************************* */

async function getStockTypes() {
  return await PrivateApiCall.get(`admin/smdItemType`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ Stock Suppliers ************************************** */
/** ******************************************************************************************* */
async function getStockSuppliers() {
  return await PrivateApiCall.get(`admin/smdSupplier`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getStockSupplier(uuid) {
  return await PrivateApiCall.get(`admin/smdSupplier/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addStockSupplier(data) {
  return await PrivateApiCall.post(`admin/smdSupplier`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStockSupplier(uuid, data) {
  return await PrivateApiCall.put(`admin/smdSupplier/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStockSupplier(uuid) {
  return await PrivateApiCall.delete(`admin/smdSupplier/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateStockSuppliers(data) {
  return await PrivateApiCall.put(`admin/smdSupplier`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************ Stripe Connect ************************************** */
/** ******************************************************************************************* */

async function createStripeSession(ruuid, buuid) {
  return await PrivateApiCall.post(`/admin/stripe/${ruuid}/${buuid}/create-session`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function createStripeAccount(ruuid, buuid) {
  return await PrivateApiCall.post(`/admin/stripe/${ruuid}/${buuid}/account`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getStripeAccount(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/stripe/${ruuid}/${buuid}/account`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

/** ******************************************************************************************* */
/** ************************************** Delivery Sources *********************************** */
/** ******************************************************************************************* */

async function getDeliverySources(ruuid) {
  return await PrivateApiCall.get(`/admin/deliverySource/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function getDeliverySource(ruuid, uuid) {
  return await PrivateApiCall.get(`/admin/deliverySource/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function addDeliverySource(ruuid, data) {
  return await PrivateApiCall.post(`/admin/deliverySource/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function bulkAddDeliverySources(ruuid, data) {
  return await PrivateApiCall.post(`/admin/deliverySource/${ruuid}/bulk-add`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error));
}

async function editDeliverySource(ruuid, uuid, data) {
  return await PrivateApiCall.put(`/admin/deliverySource/${ruuid}/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateDeliverySources(ruuid, data) {
  return await PrivateApiCall.put(`/admin/deliverySource/${ruuid}/bulk-update`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function sortDeliverySources(ruuid, deliverySources) {
  return await PrivateApiCall.put(`/admin/deliverySource/${ruuid}/sort`, { deliverySources })
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteDeliverySource(ruuid, data) {
  return await PrivateApiCall.delete(`/admin/deliverySource/${ruuid}/${data}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateSourcesImages(ruuid, data) {
  return await PrivateApiCall.put(`/admin/deliverySource/images/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPriceLevelsByBranch(ruuid) {
  return await PrivateApiCall.get(`/admin/deliverySource/${ruuid}/price-levels-by-branch`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** Activity Logs *********************************** */
/** ******************************************************************************************* */

async function getActivityLogs(query) {
  return await PrivateApiCall.get(`/admin/activities/${query ? query : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPages() {
  return await PrivateApiCall.get(`/admin/activities/pages`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** Void Reasons *************************************** */
/** ******************************************************************************************* */

async function getVoidReasons() {
  return await PrivateApiCall.get(`/admin/voidReason/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addVoidReason(data) {
  return await PrivateApiCall.post(`/admin/voidReason/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editVoidReason(uuid, data) {
  return await PrivateApiCall.put(`/admin/voidReason/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteVoidReason(uuid) {
  return await PrivateApiCall.delete(`/admin/voidReason/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** Transfer Reasons *************************************** */
/** ******************************************************************************************* */

async function getTransferReasons() {
  return await PrivateApiCall.get(`/admin/transferReason/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addTransferReason(data) {
  return await PrivateApiCall.post(`/admin/transferReason/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editTransferReason(uuid, data) {
  return await PrivateApiCall.put(`/admin/transferReason/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteTransferReason(uuid) {
  return await PrivateApiCall.delete(`/admin/transferReason/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** Kiosk Delete Reasons *************************************** */
/** ******************************************************************************************* */

async function getKioskDeleteReasons() {
  return await PrivateApiCall.get(`/admin/kioskDeleteReason/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addKioskDeleteReason(data) {
  return await PrivateApiCall.post(`/admin/kioskDeleteReason/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editKioskDeleteReason(uuid, data) {
  return await PrivateApiCall.put(`/admin/kioskDeleteReason/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteKioskDeleteReason(uuid) {
  return await PrivateApiCall.delete(`/admin/kioskDeleteReason/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** Refund Reasons *************************************** */
/** ******************************************************************************************* */

async function getRefundReasons() {
  return await PrivateApiCall.get(`/admin/refundReason/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addRefundReason(data) {
  return await PrivateApiCall.post(`/admin/refundReason/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editRefundReason(uuid, data) {
  return await PrivateApiCall.put(`/admin/refundReason/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteRefundReason(uuid) {
  return await PrivateApiCall.delete(`/admin/refundReason/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** REPORTS ******************************************** */
/** ******************************************************************************************* */

async function getOrdersReport(url) {
  return await PrivateApiCall.get(`/admin/reports/orders${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsReport(url) {
  return await PrivateApiCall.get(`/admin/reports/products${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsSimple(url) {
  return await PrivateApiCall.get(`/admin/reports/products-simple${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsSummaryByCategory(url) {
  return await PrivateApiCall.get(`/admin/reports/products-summary-by-category${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsSummaryByProductGroup(url) {
  return await PrivateApiCall.get(`/admin/reports/products-summary-by-product-group${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNotSoldProducts(url) {
  return await PrivateApiCall.get(`/admin/reports/not-sold-products${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsWithModifiers(url) {
  return await PrivateApiCall.get(`/admin/reports/products-with-modifiers${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getModifierSales(url) {
  return await PrivateApiCall.get(`/admin/reports/modifier-sales${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTopSoldProducts(url) {
  return await PrivateApiCall.get(`/admin/reports/top-sold-products${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getLeastSoldProducts(url) {
  return await PrivateApiCall.get(`/admin/reports/least-sold-products${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTopSoldProductGroups(url) {
  return await PrivateApiCall.get(`/admin/reports/top-sold-product-groups${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getProductsSimpleByOrderType(url) {
  return await PrivateApiCall.get(`/admin/reports/products-simple-by-order-type${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomersReport(url) {
  return await PrivateApiCall.get(`/admin/reports/customers${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomerProducts(url) {
  return await PrivateApiCall.get(`/admin/reports/customer-products${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getInactiveCustomers(url) {
  return await PrivateApiCall.get(`/admin/reports/inactive-customers${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getNeverOrderCustomers(url) {
  return await PrivateApiCall.get(`/admin/reports/never-order-customers${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRevenueReport(url) {
  return await PrivateApiCall.get(`/admin/reports/revenue${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPaymentReport(url) {
  return await PrivateApiCall.get(`/admin/reports/payment${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getComboSalesReport(url) {
  return await PrivateApiCall.get(`/admin/reports/combo-sales${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getComboItemSalesReport(url) {
  return await PrivateApiCall.get(`/admin/reports/combo-item-sales${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTodaysSalesReport(url = '') {
  return await PrivateApiCall.get(`/admin/reports/today-hourly-breakdown${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTodaysSalesSummary(url = '') {
  return await PrivateApiCall.get(`/admin/reports/today-summary${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTodaysSalesProducts(url = '') {
  return await PrivateApiCall.get(`/admin/reports/today-products-simple${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTodaysSalesOrders(url = '') {
  return await PrivateApiCall.get(`/admin/reports/today-orders${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByTimeReport(url) {
  return await PrivateApiCall.get(`/admin/reports/sales-by-time${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByTimeByItem(url) {
  return await PrivateApiCall.get(`/admin/reports/sales-by-time-by-item${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByHour(url) {
  return await PrivateApiCall.get(`/admin/reports/sales-by-hour${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByHourByItem(url) {
  return await PrivateApiCall.get(`/admin/reports/sales-by-hour-by-item${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTimePeriods() {
  return await PrivateApiCall.get(`/admin/reports/time-periods`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addTimePeriod(data) {
  return await PrivateApiCall.post(`/admin/reports/time-periods`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateTimePeriod(uuid, data) {
  return await PrivateApiCall.put(`/admin/reports/time-periods/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteTimePeriod(uuid) {
  return await PrivateApiCall.delete(`/admin/reports/time-periods/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSalesByItemCustomer(url) {
  return await PrivateApiCall.get(`/admin/reports/sales-by-item-customer${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCustomersListReport(url) {
  return await PrivateApiCall.get(`/admin/reports/customers-list${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDiscountCategoriesReport(url) {
  return await PrivateApiCall.get(`/admin/reports/discount-categories${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDiscountByOrderReport(url) {
  return await PrivateApiCall.get(`/admin/reports/discount-by-order${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPromoByOrderReport(url) {
  return await PrivateApiCall.get(`/admin/reports/promo-by-order${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPromoCodesReport(url) {
  return await PrivateApiCall.get(`/admin/reports/promo-codes${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDiscountByItemReport(url) {
  return await PrivateApiCall.get(`/admin/reports/discount-by-item${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPromoCodesItemsReport(url) {
  return await PrivateApiCall.get(`/admin/reports/promo-codes-items${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCreditCardReport(url) {
  return await PrivateApiCall.get(`/admin/reports/credit-card${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getVoidsReport(url) {
  return await PrivateApiCall.get(`/admin/reports/voids${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getRefundsReport(url) {
  return await PrivateApiCall.get(`/admin/reports/refunds${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTipsReport(url) {
  return await PrivateApiCall.get(`/admin/reports/tips${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTaxesReport(url) {
  return await PrivateApiCall.get(`/admin/reports/taxes${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDeliveryReport(url) {
  return await PrivateApiCall.get(`/admin/reports/delivery${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDeliverySourceReport(url) {
  return await PrivateApiCall.get(`/admin/reports/delivery-source${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getItemsTransferReport(url) {
  return await PrivateApiCall.get(`/admin/reports/items-transfer${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getTablesTransferReport(url) {
  return await PrivateApiCall.get(`/admin/reports/tables-transfer${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getOpeningClosingReport(url) {
  return await PrivateApiCall.get(`/admin/reports/opening-closing${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getMenuEngineeringReport(url) {
  return await PrivateApiCall.get(`/admin/reports/menu-engineering${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getInvoicesCostProfitReport(url) {
  return await PrivateApiCall.get(`/admin/reports/invoices-cost-profit${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getDriversReport(url) {
  return await PrivateApiCall.get(`/admin/reports/drivers${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPrintMenuReport(url) {
  return await PrivateApiCall.get(`/admin/reports/print-menu${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getEmployeeOrderingClosingReport(url) {
  return await PrivateApiCall.get(`/admin/reports/employee-ordering-closing${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAuditTrailReport(url) {
  return await PrivateApiCall.get(`/admin/reports/audit-trail${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getKitchenPerformanceReport(url) {
  return await PrivateApiCall.get(`/admin/reports/kitchen-performance${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** POS REPORTS **************************************** */
/** ******************************************************************************************* */
async function getPosDayClosingReports(url = '') {
  return await PrivateApiCall.get(`/admin/pos-reports/day-closing${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosDayClosingReportByUuid(uuid, branchUuid) {
  const query = branchUuid ? `?branchUuid=${encodeURIComponent(branchUuid)}` : ''
  return await PrivateApiCall.get(`/admin/pos-reports/day-closing/${uuid}${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosSalesRollups(url = '') {
  return await PrivateApiCall.get(`/admin/pos-reports/sales-rollup${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosSalesRollupByUuid(uuid, branchUuid) {
  const query = branchUuid ? `?branchUuid=${encodeURIComponent(branchUuid)}` : ''
  return await PrivateApiCall.get(`/admin/pos-reports/sales-rollup/${uuid}${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosDriverRollups(url = '') {
  return await PrivateApiCall.get(`/admin/pos-reports/driver-rollup${url}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosDriverRollupByUuid(uuid, branchUuid) {
  const query = branchUuid ? `?branchUuid=${encodeURIComponent(branchUuid)}` : ''
  return await PrivateApiCall.get(`/admin/pos-reports/driver-rollup/${uuid}${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

const POS_REPORTS_BASE = '/admin/pos-reports'

/** @param {{ branchUuid: string, restaurantUuid: string }} scope */
function buildPosReportsQueryString(scope, extra = {}) {
  const params = new URLSearchParams()
  if (scope?.branchUuid) params.set('branchUuid', scope.branchUuid)
  if (scope?.restaurantUuid) params.set('restaurantUuid', scope.restaurantUuid)
  Object.entries(extra || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') params.set(k, String(v))
  })
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

async function getAdminDailySalesRollup(businessDate, scope, { lang, refresh } = {}) {
  const extra = {}
  if (lang) extra.lang = lang
  if (refresh) extra.refresh = refresh
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(
    `${POS_REPORTS_BASE}/daily-rollups/sales/${encodeURIComponent(businessDate)}${query}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminDailySalesRollupRange(from, to, scope, { lang } = {}) {
  const extra = { from, to }
  if (lang) extra.lang = lang
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/daily-rollups/sales-range${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminDailyDriverRollup(businessDate, scope, { refresh } = {}) {
  const extra = refresh ? { refresh } : {}
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(
    `${POS_REPORTS_BASE}/daily-rollups/drivers/${encodeURIComponent(businessDate)}${query}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminDailyDriverRollupRange(from, to, scope) {
  const query = buildPosReportsQueryString(scope, { from, to })
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/daily-rollups/drivers-range${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminDayClosingReports(scope, { limit, offset } = {}) {
  const extra = {}
  if (limit != null) extra.limit = limit
  if (offset != null) extra.offset = offset
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/day-closing-reports/${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminDayClosingReport(uuid, scope) {
  const query = buildPosReportsQueryString(scope)
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/day-closing-reports/${uuid}${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Rebuild closed-day snapshot from live cloud orders and persist (POS recompile concept). */
async function recompileAdminDayClosingReport(uuid, scope) {
  const query = buildPosReportsQueryString(scope)
  return await PrivateApiCall.post(
    `${POS_REPORTS_BASE}/day-closing-reports/${encodeURIComponent(uuid)}/recompile${query}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Rebuild open-day (ephemeral) sales rollup from live cloud orders and persist. */
async function recompileAdminDailySalesRollup(businessDate, scope) {
  const query = buildPosReportsQueryString(scope)
  return await PrivateApiCall.post(
    `${POS_REPORTS_BASE}/daily-rollups/sales/${encodeURIComponent(businessDate)}/recompile${query}`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminUnclosedWorkingDays(scope, { beforeDate } = {}) {
  const extra = beforeDate ? { beforeDate } : {}
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/day-closing-reports/unclosed-dates${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getAdminOpenBusinessDaysSummary(scope, { calendarToday } = {}) {
  const extra = calendarToday ? { calendarToday } : {}
  const query = buildPosReportsQueryString(scope, extra)
  return await PrivateApiCall.get(`${POS_REPORTS_BASE}/day-closing-reports/open-days-summary${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** POS MINIMUM CHARGE ******************************** */
/** ******************************************************************************************* */
async function getMinimumCharges(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/pos-min-charge/${ruuid}${buuid ? `/${buuid}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addOrEditMinimumCharge(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/pos-min-charge/${ruuid}${buuid ? `/${buuid}` : ''}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteMinimumCharge(ruuid, uuid) {
  return await PrivateApiCall.delete(`/admin/pos-min-charge/${ruuid}/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** RECEIPT PRINT SETTINGS **************************** */
/** ******************************************************************************************* */
async function getReceiptPrintSettings(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/receipt-print-settings/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function upsertReceiptPrintSettings(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/receipt-print-settings/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getOrderPrintSettings(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/order-print-settings/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function upsertOrderPrintSettings(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/order-print-settings/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** STOCK LISTS **************************************** */
/** ******************************************************************************************* */
async function sortStockLists(data) {
  return await PrivateApiCall.put(`/admin/stock-lists/sort`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** *********************************** Stock Brand  ************************************* */
/** ******************************************************************************************* */
async function getStockBrands() {
  return await PrivateApiCall.get(`admin/stock-brand`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getStockBrand(uuid) {
  return await PrivateApiCall.get(`admin/stock-brand/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function addStockBrand(data) {
  return await PrivateApiCall.post(`admin/stock-brand`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function editStockBrand(uuid, data) {
  return await PrivateApiCall.put(`admin/stock-brand/${uuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteStockBrand(uuid) {
  return await PrivateApiCall.delete(`admin/stock-brand/${uuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** POS CONFIGURAWTION SETTINGS *********************************** */
/** ******************************************************************************************* */
async function getPosConfiguration(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/general-config/pos-settings/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function setPosConfiguration(ruuid, buuid, data) {
  return await PrivateApiCall.put(`/admin/general-config/pos-settings/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** POS SALE INVENTORY (EOD SKIPS) ********************* */
/** ******************************************************************************************* */
async function getPosSaleInventoryEodSkips(ruuid, params = {}) {
  const q = new URLSearchParams()
  if (params.buuid) q.set('buuid', params.buuid)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  const qs = q.toString()
  return await PrivateApiCall.get(`/admin/pos-sale-inventory/${ruuid}/eod-skips${qs ? `?${qs}` : ''}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPosSaleInventoryDeductedMovements(ruuid, detailUuid) {
  return await PrivateApiCall.get(
    `/admin/pos-sale-inventory/${ruuid}/order-details/${detailUuid}/movements`
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function resolvePosSaleInventoryVirtualLocation(ruuid, buuid, data) {
  return await PrivateApiCall.post(
    `/admin/pos-sale-inventory/${ruuid}/${buuid}/resolve-virtual-location`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function resolvePosSaleInventoryPhysicalLocation(ruuid, buuid, data) {
  return await PrivateApiCall.post(
    `/admin/pos-sale-inventory/${ruuid}/${buuid}/resolve-physical-location`,
    data
  )
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function retryPosSaleInventory(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/pos-sale-inventory/${ruuid}/${buuid}/retry`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getSmdLocations(buuid) {
  const query = buuid && buuid !== '0' ? `?buuid=${buuid}` : ''
  return await PrivateApiCall.get(`/admin/SmdLocation${query}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** COURSE SETTINGS *********************************** */
/** ******************************************************************************************* */
async function getCourseSettings(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/course-settings/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCourseSettingsByRestaurant(ruuid) {
  return await PrivateApiCall.get(`/admin/course-settings/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function upsertCourseSettings(ruuid, buuid, data) {
  return await PrivateApiCall.post(`/admin/course-settings/${ruuid}/${buuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// Course APIs
async function getCourses(ruuid, buuid) {
  return await PrivateApiCall.get(`/admin/courses/${ruuid}/${buuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCoursesByRestaurant(ruuid) {
  return await PrivateApiCall.get(`/admin/courses/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getCourse(ruuid, buuid, courseUuid) {
  return await PrivateApiCall.get(`/admin/courses/${ruuid}/${buuid}/${courseUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function createCourse(ruuid, data) {
  return await PrivateApiCall.post(`/admin/courses/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateCourse(ruuid, buuid, courseUuid, data) {
  return await PrivateApiCall.put(`/admin/courses/${ruuid}${buuid ? `/${buuid}` : ''}/${courseUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteCourse(ruuid, buuid, courseUuid) {
  return await PrivateApiCall.delete(`/admin/courses/${ruuid}/${buuid}/${courseUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function orderCourses(ruuid, buuid, data) {
  return await PrivateApiCall.put(`/admin/courses/${ruuid}/${buuid}/order`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function assignProductsFromGroupsToCourse(ruuid, courseUuid, data) {
  return await PrivateApiCall.post(`/admin/courses/${ruuid}/${courseUuid}/assign-products-from-groups`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** ******************************************************************************************* */
/** ************************************** PRICE LEVELS ************************************** */
/** ******************************************************************************************* */
async function getPriceLevelProducts(ruuid, priceLevelUuid = null) {
  const url = priceLevelUuid
    ? `/admin/price-level/${ruuid}/products/base-prices?priceLevelUuid=${priceLevelUuid}`
    : `/admin/price-level/${ruuid}/products/base-prices`
  return await PrivateApiCall.get(url)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPriceLevelProductGroups(ruuid) {
  return await PrivateApiCall.get(`/admin/price-level/${ruuid}/product-groups`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdateBasePrices(ruuid, data) {
  return await PrivateApiCall.put(`/admin/price-level/${ruuid}/bulk-update-base-prices`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateBasePricesByCriteria(ruuid, data) {
  return await PrivateApiCall.put(`/admin/price-level/${ruuid}/update-base-prices-by-criteria`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updatePriceLevelPricesByCriteria(ruuid, data) {
  return await PrivateApiCall.put(`/admin/price-level/${ruuid}/update-price-level-prices-by-criteria`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function bulkUpdatePriceLevelPrices(ruuid, data) {
  return await PrivateApiCall.put(`/admin/price-level/${ruuid}/bulk-update-prices`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

// Price Level CRUD Operations
async function getPriceLevels(ruuid) {
  return await PrivateApiCall.get(`/admin/price-level/${ruuid}/`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function getPriceLevel(ruuid, puuid) {
  return await PrivateApiCall.get(`/admin/price-level/${ruuid}/${puuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function createPriceLevel(ruuid, data) {
  return await PrivateApiCall.post(`/admin/price-level/${ruuid}/`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updatePriceLevel(ruuid, puuid, data) {
  return await PrivateApiCall.put(`/admin/price-level/${ruuid}/${puuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deletePriceLevel(ruuid, puuid) {
  return await PrivateApiCall.delete(`/admin/price-level/${ruuid}/${puuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Item-label templates (named presets; the default first) — Products → Print Labels. */
async function getLabelPrintPresets(ruuid) {
  return await PrivateApiCall.get(`admin/label-print-settings/presets/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Saved label print lists ("print later") of one catalogue page: stockItem | purchasedProduct | product. */
async function getLabelPrintLists(ruuid, source) {
  return await PrivateApiCall.get(`admin/label-print-settings/lists/${ruuid}?source=${encodeURIComponent(source || "")}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function createLabelPrintList(ruuid, data) {
  return await PrivateApiCall.post(`admin/label-print-settings/lists/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateLabelPrintList(ruuid, listUuid, data) {
  return await PrivateApiCall.put(`admin/label-print-settings/lists/${ruuid}/${listUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteLabelPrintList(ruuid, listUuid) {
  return await PrivateApiCall.delete(`admin/label-print-settings/lists/${ruuid}/${listUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** The single label template from before presets — fallback until the presets API is deployed. */
async function getLabelPrintSettings(ruuid) {
  return await PrivateApiCall.get(`admin/label-print-settings/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Product-label templates (named presets; the default first) — Products → Print Labels prints with these. */
async function getProductLabelPrintPresets(ruuid) {
  return await PrivateApiCall.get(`admin/product-label-print-settings/presets/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** POS › Print Network › Product label design (RESTOAPP-7366): the preset editor's writes. */
async function createProductLabelPrintPreset(ruuid, data) {
  return await PrivateApiCall.post(`admin/product-label-print-settings/presets/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateProductLabelPrintPreset(ruuid, presetUuid, data) {
  return await PrivateApiCall.put(`admin/product-label-print-settings/presets/${ruuid}/${presetUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteProductLabelPrintPreset(ruuid, presetUuid) {
  return await PrivateApiCall.delete(`admin/product-label-print-settings/presets/${ruuid}/${presetUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

/** Saved product-label print lists — always the Products page's (`source` is pinned server-side). */
async function getProductLabelPrintLists(ruuid) {
  return await PrivateApiCall.get(`admin/product-label-print-settings/lists/${ruuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function createProductLabelPrintList(ruuid, data) {
  return await PrivateApiCall.post(`admin/product-label-print-settings/lists/${ruuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function updateProductLabelPrintList(ruuid, listUuid, data) {
  return await PrivateApiCall.put(`admin/product-label-print-settings/lists/${ruuid}/${listUuid}`, data)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

async function deleteProductLabelPrintList(ruuid, listUuid) {
  return await PrivateApiCall.delete(`admin/product-label-print-settings/lists/${ruuid}/${listUuid}`)
    .then((response) => response)
    .catch((error) => errorCatch(error))
}

export {
  getLabelPrintPresets,
  getLabelPrintLists,
  createLabelPrintList,
  updateLabelPrintList,
  deleteLabelPrintList,
  getLabelPrintSettings,
  getProductLabelPrintPresets,
  createProductLabelPrintPreset,
  updateProductLabelPrintPreset,
  deleteProductLabelPrintPreset,
  getProductLabelPrintLists,
  createProductLabelPrintList,
  updateProductLabelPrintList,
  deleteProductLabelPrintList,
  //DIRECT MESSAGES
  getDirectMessagesWithCustomers,
  getDirectMessagesJobs,
  deleteDirectMessage,
  editDirectMessage,
  addDirectMessage,
  getDirectMessage,
  getDirectMessages,
  getCustomersBasedOnCriteria,
  //EMAIL-SETUP
  getEmailServer,
  addEmailServer,
  deleteEmailServer,
  testEmail,
  //CURRENCIES
  getRates,
  getRate,
  addRate,
  editRate,
  deleteRate,
  generateRates,
  bulkAddCurrencyRates,
  bulkUpdateCurrencyRates,
  updateCurrencyImages,
  exportCurrencyExcel,
  importCurrencyExcel,
  //PAYMENTS
  addDefaultPayment,
  getPayments,
  getPayment,
  editPayment,
  deletePayment,
  addPayment,
  sortPayments,
  bulkAddPayments,
  bulkUpdatePayments,
  updatePaymentImages,
  exportPaymentsExcel,
  importPaymentsExcel,
  getPaymentBills,
  hidePaymentBill,
  bulkHidePaymentBills,
  unhidePaymentBill,
  //DASHBOARD
  getTopSpenders,
  getAvgOrderPrice,
  getLateOrders,
  getTotalOrders,
  getNewCustomers,
  getCanceledOrders,
  getSalesByZone,
  getTotalCustomers,
  getSalesByAffiliate,
  getSignupsByAffiliate,
  getTotalDiscounts,
  getTotalPromoDiscounts,
  getTopXItemsQty,
  getTopXItemsAmt,
  getNotActiveCustomers,
  getFeedbacksAndComplaints,
  getFeedbacksRating,
  getTotalRevenuePerMonth,
  getNewProduct,
  getCustomerCountValue,
  getCustomerCountOrder,
  getQrCount,
  getNewProductsList,
  getFeedbacksSummary,
  getBirthdayCount,
  getDeliveryStatus,
  getPosSyncStatus,
  getBusinessDaysStatus,
  getNotSoldItems,
  getSalesByRestaurant,
  getSalesByBranch,
  getAffiliateClicks,
  getOrdersStatus,
  getTopItemsBranch,
  getAvgOrders,
  getSalesByCategory,
  getSalesByCustomer,
  getAvgDeliveryTime,
  getAvgPrepTime,
  getRestaurantsCurrency,
  setConsolidatedCurrency,
  getConsolidatedCurrency,
  setCardsPositioning,
  getCardsPositioning,
  getNewDesignLayout,
  setNewDesignLayout,
  getNewDesignDashboardData,
  // NOTIFICATIONS
  registerDevice,
  getNotifications,
  viewNotification,
  confirmNotification,
  //MESSAGES
  deleteChat,
  getUserChats,
  addUpdateChat,
  getChatMessages,
  updateMessage,
  deleteMessage,
  getUserGlobalChats,
  getMessagesRestaurantsAndBranches,
  //AUTOMATED-MESSAGES
  getAutomatedMessages,
  getAutomatedMessage,
  getAutomatedJobs,
  addAutomatedMessage,
  editAutomatedMessage,
  deleteAutomatedMessage,
  //PRODUCTS-AVAILABILITY
  getProductsAvailability,
  deleteProductAvailability,
  updateProductsAvailability,
  //QR
  addQR,
  addQrTables,
  addQrButton,
  bulkAddQrButtons,
  editQrButton,
  bulkEditQrButtons,
  deleteQrButton,
  deleteQrTable,
  getQrButton,
  getQrButtons,
  getQr,
  validateURL,
  getQrTables,
  getTableOrderingSettings,
  addEditTableOrderingSettings,
  getQrOnlineDelivery,
  addOnlineDeliveryQr,
  getQROrderingSettings,
  setQROrderingSettings,
  //ENTITY QR
  //qr
  getEntityQr,
  addEditEntityQr,
  //qr tables
  getEntityQrTables,
  addEntityQrTables,
  deleteEntityQrTable,
  bulkAddEntityQrButtons,
  bulkUpdateEntityQrButtons,
  //qr buttons
  getEntityQrButtons,
  getEntityQrButton,
  addEntityQrButton,
  editEntityQrButton,
  deleteEntityQrButton,
  // AUTH
  login,
  forgotPass,
  otp,
  verifyOTP,
  // ME INFO
  getMeProfile,
  editMeProfile,
  // Admin PROFILES
  getAdminProfiles,
  addAdminProfile,
  editAdminProfile,
  deleteAdminProfile,
  // RESTAURANT
  getRestaurants,
  addRestaurant,
  editRestaurant,
  deleteRestaurant,
  // BRANCHES
  getBranches,
  getAllBranches,
  addBranch,
  editBranch,
  deleteBranch,
  getBranchesTableOrdering,
  getBranchesLoyalty,
  getBranchesQRMenu,
  getBranchesByModule,
  getBranchesOnlineDelivery,
  getBranchesPos,
  getBranchesAiChatbot,
  getBranchesOrders,
  // AI CHATBOT SETTING
  getAllAiChatbotSettings,
  getAiChatbotSettings,
  addEditAiChatbotSettings,
  // AI CHATBOT CONVERSATION
  getAiChatbotConversations,
  getAiChatbotConversation,
  // ENTITIES
  getEntities,
  addEntity,
  editEntity,
  bulkUpdateEntities,
  deleteEntity,
  // GLOBAL CONFIG
  getGlobalConfig,
  uploadImage,
  uploadGlb,
  uploadFile,
  bulkUploadImages,
  uploadQr,
  // CATEGORY
  getCategories,
  addCategory,
  editCategory,
  deleteCategory,
  bulkUpdateCategories,
  importCategories,
  updateCategoryImages,
  bulkAddCategories,
  // GROUPS
  getGroups,
  addGroup,
  editGroup,
  deleteGroup,
  updateGroups,
  bulkEditGroups,
  bulkAddGroups,
  exportGroupsExcel,
  importGroupsExcel,
  // PRODUCTS
  getProducts,
  getProductsPaginated,
  getProduct,
  getProductMaxPrice,
  addProduct,
  updateProducts,
  updateProductMediaGallery,
  editProduct,
  updateProductPriceLevelPrices,
  deleteProduct,
  deleteProductImage,
  editProductImage,
  editBulkProducts,
  addBulkProducts,
  getRewardsProducts,
  getAddEditProducts,
  generateImage,
  uploadProductMediaGallery,
  importProducts,
  getProductExelTemplate,
  // TAXES
  getTaxes,
  addTax,
  editTax,
  deleteTax,
  getBranchTaxes,
  updateBranchTaxes,
  getBranchChargeTaxes,
  updateBranchChargeTaxes,
  applyTaxesToProducts,
  getProductsWithTaxes,
  applyTaxesToAllProducts,
  getStockItemsWithTaxes,
  applyTaxesToStockItems,
  getPurchasedProductsWithTaxes,
  applyTaxesToPurchasedProducts,
  getSuppliersWithTaxes,
  applyTaxesToSuppliers,
  getModifiersWithTaxes,
  applyTaxesToModifiers,
  // Ingredients
  getIngredients,
  addIngredient,
  editIngredient,
  deleteIngredient,
  bulkUpdateIngredients,
  updateIngredientImages,
  bulkAddIngredients,
  exportIngredientsExcel,
  importIngredientsExcel,
  // TYPES AND ALLERGIES
  getTypesAndAllergies,
  getSingleTypeAndAllergies,
  addTypesAndAllergies,
  editTypesAndAllergies,
  deleteTypesAndAllergies,
  updateTypesAndAllergies,
  bulkUpdateTypes,
  bulkAddTypes,
  exportFoodTypesExcel,
  importFoodTypesExcel,
  // MODIFIERS
  getModifiers,
  getModifier,
  addModifier,
  editModifier,
  deleteModifier,
  updateModifiers,
  bulkUpdateModifiers,
  bulkAddModifiers,
  exportModifiersExcel,
  importModifiersExcel,
  // MODIFIERSGROUPS
  getModifiersGroups,
  getModifierGroup,
  getModifierGroupWithModifiers,
  getModifiersGroupsWithModifiers,
  addModifierGroup,
  editModifierGroup,
  deleteModifierGroup,
  bulkUpdateModifierGroups,
  bulkAddModifierGroups,
  exportModGroupsExcel,
  importModGroupsExcel,
  setModifierGroupImages,
  // MENU
  getMenu,
  getMenus,
  getMenuAssignments,
  getAllMenuAssignments,
  bulkUpsertMenuAssignments,
  bulkUpsertMenuAssignmentsMultiRestaurant,
  addMenu,
  editMenu,
  deleteMenu,
  editMenuName,
  updateMenuSorting,
  listMenuImports,
  getMenuImport,
  createMenuImportMultipart,
  createMenuImportFromUrls,
  createMenuImportFromWebsite,
  patchMenuImportDraft,
  confirmMenuImport,
  rejectMenuImport,
  deleteMenuImport,
  globalSearch,
  globalSearchWarm,
  aiAgentChat,
  aiAgentVoiceTranscript,
  aiAgentVoiceCall,
  aiAgentVoiceTool,
  aiAgentVoiceEvent,
  // TRANSLATIONS
  getTranslations,
  getTranslationPages,
  editTranslations,
  aiTranslate,
  aiGenerateDescription,
  getSupportedLanguagesSettings,
  editSupportedLanguagesSettings,
  getSupportedLanguages,
  getMissingTranslations,
  getAutomaticTranslationState,
  setAutomaticTranslationState,
  //Roles
  getRoles,
  addRole,
  editRole,
  deleteRole,
  getRolesHierarchy,
  editRolesHierarchy,
  assignRoleUsers,
  getRolePosPermissions,
  updateRolePosPermissions,
  //Users
  getUsers,
  getCombinedUsers,
  addUser,
  addCombinedUsers,
  editUser,
  editCombinedUsers,
  deleteUser,
  exportCombinedUsersExcel,
  importCombinedUsersExcel,
  updateAdminUsersImages,
  editUserSettings,
  changeAccountActivity,
  changeAccountVerification,
  changeUserDeviceRestriction,
  getUserDevices,
  approveUserDevice,
  rejectUserDevice,
  deleteUserDevice,
  setUserPunchDevice,
  getSmaAttendanceSettings,
  updateSmaAttendanceSettings,
  getSmaAttendanceZones,
  saveSmaAttendanceZones,
  getStaffScheduleConfig,
  addStaffShiftType,
  editStaffShiftType,
  deleteStaffShiftType,
  addStaffDepartment,
  editStaffDepartment,
  deleteStaffDepartment,
  saveStaffRequirements,
  saveStaffShiftLimits,
  getStaffSchedule,
  saveStaffAssignments,
  saveStaffDayRequirements,
  copyStaffWeek,
  addStaffEmployeeType,
  deleteStaffEmployeeType,
  getAppUsers,
  getAppUser,
  addAppUser,
  editAppUser,
  bulkEditAppUsers,
  bulkAddAppUsers,
  updateAppUsersImages,
  deleteAppUser,
  getRestaurantsWithBranches,
  exportAppUsersExcel,
  importAppUsersExcel,
  getAllCustomerCreditTransactions,
  getCustomerCreditTransactions,
  getCustomerCreditStatement,
  getCustomerStatementPrintSettings,
  getCustomerReceiptPrintSettings,
  saveCustomerReceiptPrintSettings,
  getSmaB2bOrder,
  getSalesPrintSettings,
  postCustomerCreditPayment,
  setCustomerCreditLimit,
  getAllCustomerAging,
  getCustomerAging,
  //APP USERS ADDRESSES
  getAppUserAddresses,
  addAppUserAddress,
  editAppUserAddress,
  deleteAppUserAddress,
  parseMapLocationLink,
  //CUSTOMER COMPLAINTS
  getCustomerComplaints,
  addCustomerComplaint,
  editCustomerComplaint,
  //CUSTOMER ORDER STATS & ORDERS
  getCustomerStats,
  getCustomerOrders,
  //APP USERS GROUPS
  getAppUsersGroups,
  addAppUsersGroup,
  editAppUsersGroup,
  deleteAppUsersGroup,
  bulkAddAppUsersGroups,
  exportAppUsersGroupsExcel,
  importAppUsersGroupsExcel,
  //ALLERGENS
  getAllergens,
  getAllergen,
  addAllergen,
  editAllergen,
  deleteAllergen,
  bulkUpdateAllergens,
  bulkAddAllergens,
  updateAllergensImages,
  exportAllergensExcel,
  importAllergensExcel,
  //PRINTERS
  getPrinters,
  addPrinter,
  editPrinter,
  deletePrinter,
  //PRINTER LOCATIONS
  getPrinterLocations,
  addPrinterLocation,
  editPrinterLocation,
  deletePrinterLocation,
  addPrinterLocationForBranch,
  editPrinterLocationForBranch,
  assignLocationByBranch,
  //ZONES
  getZones,
  addZone,
  editZone,
  deleteZone,
  bulkAddZones,
  bulkEditZones,
  getPrinterLocationAssignments,
  setPrinterLocationAssignment,
  getPosInvoiceConfigs,
  setPosInvoiceConfig,
  getPosBackScreens,
  bulkSetPrinterLocations,
  setPrinterLocationOutputRouting,
  getWorkstations,
  updateWorkstation,
  getKitchenMonitors,
  getKitchenMonitor,
  editKitchenMonitor,
  getBackScreens,
  getBackScreen,
  editBackScreen,
  getSelfOrderingDevices,
  editSelfOrderingDevice,
  getPrinterLocationsConfig,
  setPrinterLocationsConfig,
  getNetworkDiagramData,
  //DELIVERY-ZONE
  getDeliveryZones,
  getDeliveryZone,
  addDeliveryZone,
  editDeliveryZone,
  deleteDeliveryZone,
  getOnlineDeliverySettings,
  getAllOnlineDeliverySettings,
  addEditDeliverySettings,
  //EVENTS
  getEvents,
  getEvent,
  addEvent,
  editEvent,
  deleteEvent,
  //MEDIA
  getAllMediaInteractions,
  getAllMedia,
  addMedia,
  editMedia,
  deleteMedia,
  hideMedia,
  showMedia,
  sortMedia,
  updateMedia,
  //BACK SCREEN MEDIA
  getAllBackScreenMedia,
  addBackScreenMedia,
  deleteBackScreenMedia,
  toggleBackScreenMedia,
  sortBackScreenMedia,
  updateBackScreenMediaFit,
  //SELF ORDERING MEDIA
  getAllSelfOrderingMedia,
  addSelfOrderingMedia,
  deleteSelfOrderingMedia,
  toggleSelfOrderingMedia,
  sortSelfOrderingMedia,
  updateSelfOrderingMediaFit,
  updateSelfOrderingMediaTextOverlay,
  editSelfOrderingBannerSettings,
  getAllSelfOrderingMarketing,
  addSelfOrderingMarketingMedia,
  deleteSelfOrderingMarketingMedia,
  toggleSelfOrderingMarketingMedia,
  sortSelfOrderingMarketingMedia,
  updateSelfOrderingMarketingMediaFit,
  editSelfOrderingMarketingSettings,
  //GALLERY
  getGallery,
  uploadGallery,
  updateLayout,
  deleteGallery,
  getGalleryCategories,
  getGalleryCategory,
  addGalleryCategory,
  editGalleryCategory,
  deleteGalleryCategory,
  //FEEDBACK
  getFeedbackTemplate,
  editFeedbackTemplate,
  editFeedbackTemplateMessage,
  getFeedback,
  addFeedback,
  editFeedbackComplaint,
  //MODULES
  getModules,
  //PLATFORMS
  getPlatforms,
  addPlatform,
  editPlatform,
  deletePlatform,
  //PROMOTIONS
  getAllPromotions,
  getPromotion,
  addPromotion,
  editPromotion,
  deletePromotion,
  //BRANCHES-FINANCE
  getBranchesFinance,
  updateBranchesFinancePrice,
  updateBranchesFinanceAvailability,
  getBranchesFinanceProduct,
  //LOYALTY
  getLoyalty,
  addEditLoyalty,
  addEditLoyaltyDescription,
  getLoyaltyDescription,
  getLoyaltyUsers,
  addLoyaltyConfig,
  getLoyaltyConfig,
  getLoyaltyMenu,
  addEditLoyaltyMenu,
  getCashback,
  getGifts,
  getGift,
  addEditCashback,
  editGift,
  addGift,
  deleteGift,
  getRewardsBasedOnLevel,
  getRewardBasedOnLevel,
  addRewardBasedOnLevel,
  editRewardBasedOnLevel,
  deleteRewardBasedOnLevel,
  editLoyaltyPoints,
  getLoyaltyUserEntries,
  //DISCOUNT
  getAllDiscount,
  getDiscount,
  addDiscount,
  editDiscount,
  deleteDiscount,
  sortDiscount,
  editDiscountSettings,
  getDiscountSettings,
  //ORDERS
  getOrders,
  checkIfOrderMeetsFilters,
  getOrder,
  getOrderActivityHistory,
  cancelOrder,
  acceptOrder,
  rejectOrder,
  completeOrder,
  dispatchOrder,
  updateDispatchSettings,
  getDrivers,
  getDriver,
  undispatchOrder,
  undispatchAllOrders,
  setBusy,
  setBusyAll,
  closeDay,
  getUnclosedDays,
  setClosed,
  setClosedAll,
  //TABLE ORDERS
  getTableOrders,
  updateTableOrderStatus,
  payTableOrder,
  //TABLE ACTIONS
  getTableActions,
  //REPORTS
  getTodayReport,
  getClosedReport,
  getReportDates,
  //REASONS
  getReasons,
  addReason,
  editReason,
  deleteReason,
  //ORDERS HISTORY
  getOrdersHistory,
  //DESIGN SETUP
  getDesignSetupGlobalSettings,
  editDesignSetupGlobalSettings,
  getDesignSetup,
  editDesignSetup,
  getDesignTemplates,
  createDesignTemplate,
  deleteDesignTemplate,
  //LOGIN DESIGN SETUP
  getLoginDesignSetup,
  editLoginDesignSetup,
  getPosLoginDesignSetup,
  editPosLoginDesignSetup,
  //SPLASH SCREEN DESIGN SETUP
  getSplashScreenDesignSetup,
  editSplashScreenDesignSetup,
  //RESTAURANTS DESIGN SETUP
  getRestaurantsDesignSetup,
  editRestaurantsDesignSetup,
  //FLOOR SETUP
  getFloors,
  getFloor,
  getTables,
  getLastTableNumberInBranch,
  editOrAddFloorsAndTables,
  updateFloorOrder,
  deleteTheFloor,
  deleteTable,
  getBranchesFloorSetup,
  //URL LINK
  getAffiliateLink,
  addAffiliateLink,
  editAffiliateLink,
  deleteAffiliateLink,
  //RECIPES
  getRecipesProducts,
  getRecipeData,
  getAllRecipes,
  getRecipeByCategory,
  getSingleRecipe,
  getSingleProductRecipes,
  addRecipe,
  bulkCreateRecipes,
  updateRecipe,
  deleteRecipe,
  getCostAnalysis,
  applyMarkup,
  getRecipeInstructions,
  updateRecipeInstructions,
  searchRecipes,
  getRecipeAnalysis,
  //STOCK
  //UOM
  getUoms,
  getConnectedUoms,
  getUom,
  addUom,
  editUom,
  deleteUom,
  bulkUpdateUoms,
  //PUOMS
  getPuoms,
  getPuom,
  addPuom,
  editPuom,
  deletePuom,
  //UOM CONVERSIONS
  getConversions,
  getConversion,
  addConversion,
  editConversion,
  deleteConversion,
  bulkUpdateConversions,
  //STOCK ITEMS
  getStockItems,
  addStockItem,
  editStockItem,
  //LINKED ITEMS
  getLinkedItems,
  getLinkedItem,
  addLinkedItem,
  editLinkedItem,
  deleteLinkedItem,
  //STOCK CATEGORIES
  getStockCategories,
  getStockCategory,
  addStockCategory,
  editStockCategory,
  deleteStockCategory,
  bulkUpdateStockCategories,
  //STOCK GROUPS
  getStockGroups,
  getStockGroup,
  addStockGroup,
  editStockGroup,
  deleteStockGroup,
  bulkUpdateStockGroups,
  //STOCK SUPPLIERS
  getStockSuppliers,
  getStockSupplier,
  addStockSupplier,
  editStockSupplier,
  deleteStockSupplier,
  bulkUpdateStockSuppliers,
  //Stock Types
  getStockTypes,
  // Stripe Connect
  createStripeSession,
  createStripeAccount,
  getStripeAccount,
  // Delivery Sources
  getDeliverySource,
  getDeliverySources,
  addDeliverySource,
  bulkAddDeliverySources,
  editDeliverySource,
  bulkUpdateDeliverySources,
  sortDeliverySources,
  deleteDeliverySource,
  getPriceLevelsByBranch,
  updateSourcesImages,
  //Pos Users
  getPosUsers,
  getPosUser,
  getPosBranches,
  getAllPosBranches,
  addPosUser,
  editPosUser,
  deletePosUser,
  updatePosUsersImages,
  //Activity Logs
  getActivityLogs,
  getPages,
  //Void Reason
  getVoidReasons,
  addVoidReason,
  editVoidReason,
  deleteVoidReason,
  //Transfer Reason
  getTransferReasons,
  addTransferReason,
  editTransferReason,
  deleteTransferReason,
  //Kiosk Delete Reason
  getKioskDeleteReasons,
  addKioskDeleteReason,
  editKioskDeleteReason,
  deleteKioskDeleteReason,
  //Refund Reason
  getRefundReasons,
  addRefundReason,
  editRefundReason,
  deleteRefundReason,
  //Reports
  getOrdersReport,
  getProductsReport,
  getProductsSimple,
  getProductsSummaryByCategory,
  getProductsSummaryByProductGroup,
  getNotSoldProducts,
  getProductsWithModifiers,
  getModifierSales,
  getTopSoldProducts,
  getLeastSoldProducts,
  getTopSoldProductGroups,
  getProductsSimpleByOrderType,
  getCustomersReport,
  getCustomerProducts,
  getInactiveCustomers,
  getNeverOrderCustomers,
  getRevenueReport,
  getPaymentReport,
  getCreditCardReport,
  getVoidsReport,
  getRefundsReport,
  getTipsReport,
  getTaxesReport,
  getDeliveryReport,
  getDeliverySourceReport,
  getItemsTransferReport,
  getTablesTransferReport,
  getOpeningClosingReport,
  getMenuEngineeringReport,
  getInvoicesCostProfitReport,
  getDriversReport,
  getPrintMenuReport,
  getEmployeeOrderingClosingReport,
  getAuditTrailReport,
  getKitchenPerformanceReport,
  getComboSalesReport,
  getComboItemSalesReport,
  getSalesByTimeReport,
  getSalesByTimeByItem,
  getSalesByHour,
  getSalesByHourByItem,
  getTimePeriods,
  addTimePeriod,
  updateTimePeriod,
  deleteTimePeriod,
  getSalesByItemCustomer,
  getCustomersListReport,
  getDiscountCategoriesReport,
  getDiscountByOrderReport,
  getPromoByOrderReport,
  getPromoCodesReport,
  getDiscountByItemReport,
  getPromoCodesItemsReport,
  //POS REPORTS
  getPosDayClosingReports,
  getPosDayClosingReportByUuid,
  getPosSalesRollups,
  getPosSalesRollupByUuid,
  getPosDriverRollups,
  getPosDriverRollupByUuid,
  buildPosReportsQueryString,
  getAdminDailySalesRollup,
  getAdminDailySalesRollupRange,
  getAdminDailyDriverRollup,
  getAdminDailyDriverRollupRange,
  getAdminDayClosingReports,
  getAdminDayClosingReport,
  recompileAdminDayClosingReport,
  recompileAdminDailySalesRollup,
  getAdminUnclosedWorkingDays,
  getAdminOpenBusinessDaysSummary,
  getTodaysSalesReport,
  getTodaysSalesSummary,
  getTodaysSalesProducts,
  getTodaysSalesOrders,
  //POS MINIMUM CHARGE
  getMinimumCharges,
  addOrEditMinimumCharge,
  deleteMinimumCharge,
  getPosConfiguration,
  setPosConfiguration,
  getPosSaleInventoryEodSkips,
  getPosSaleInventoryDeductedMovements,
  resolvePosSaleInventoryVirtualLocation,
  resolvePosSaleInventoryPhysicalLocation,
  retryPosSaleInventory,
  getSmdLocations,
  //RECEIPT PRINT SETTINGS
  getReceiptPrintSettings,
  upsertReceiptPrintSettings,
  //ORDER PRINT SETTINGS
  getOrderPrintSettings,
  upsertOrderPrintSettings,
  //STOCK LISTS
  sortStockLists,
  //STOCK BRAND
  getStockBrands,
  getStockBrand,
  addStockBrand,
  editStockBrand,
  deleteStockBrand,
  //COURSE SETTINGS
  getCourseSettings,
  getCourseSettingsByRestaurant,
  upsertCourseSettings,
  getCourses,
  getCoursesByRestaurant,
  getCourse,
  createCourse,
  updateCourse,
  deleteCourse,
  orderCourses,
  assignProductsFromGroupsToCourse,
  //VIRTUAL LOCATIONS
  getVirtualLocations,
  assignProductsToVirtualLocation,
  //PRINTER LOCATIONS
  getPrinterLocationProducts,
  getPrinterLocationProductGroups,
  bulkUpdateProductLocations,
  //PRICE LEVELS
  getPriceLevelProducts,
  getPriceLevelProductGroups,
  bulkUpdateBasePrices,
  updateBasePricesByCriteria,
  updatePriceLevelPricesByCriteria,
  bulkUpdatePriceLevelPrices,
  // PRICE LEVELS CRUD
  getPriceLevels,
  getPriceLevel,
  createPriceLevel,
  updatePriceLevel,
  deletePriceLevel,
};
